import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  FormControlLabel,
  InputLabel,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Grid,
  Card,
  CardContent,
  Switch,
  Toolbar,
  IconButton,
  Tooltip,
  Menu,
  ListItemText,
  ListItemIcon,
  CircularProgress,
  Autocomplete
} from '@mui/material';
import {
  Refresh,
  Search,
  FilterList,
  Download,
  Cancel,
  RestartAlt,
  Visibility,
  Delete,
  Edit,
  MoreVert,
  CheckCircle,
  Block,
  LocalShipping,
  GetApp,
  SelectAll
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

// Debounce utility function
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// ProductImage component to fetch and display product images
const ProductImage = ({ sku, title }) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchProductImage = async () => {
      if (!sku) {
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`/api/inventory/product/image/${encodeURIComponent(sku)}`);
        if (response.data.image_url) {
          setImageUrl(response.data.image_url);
        }
      } catch (err) {
        console.error('Failed to fetch product image:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchProductImage();
  }, [sku]);

  if (loading) {
    return (
      <Box sx={{ 
        width: 60, 
        height: 60, 
        backgroundColor: '#f5f5f5',
        borderRadius: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1px solid rgba(255,255,255,0.1)'
      }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (error || !imageUrl) {
    return (
      <Box sx={{ 
        width: 60, 
        height: 60, 
        backgroundColor: 'rgba(255,255,255,0.05)',
        borderRadius: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1px solid rgba(255,255,255,0.1)',
        flexShrink: 0
      }}>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.6rem', textAlign: 'center' }}>
          No Image
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ flexShrink: 0 }}>
      <img
        src={imageUrl}
        alt={title}
        style={{
          width: 60,
          height: 60,
          objectFit: 'cover',
          borderRadius: 4,
          border: '1px solid rgba(255,255,255,0.1)'
        }}
        onError={() => setError(true)}
      />
    </Box>
  );
};

const OrdersPage = () => {
  const { isUser } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // 🆕 NEW: Pagination state
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25
  });
  const [totalOrders, setTotalOrders] = useState(0);
  
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    fulfillmentCenter: '',
    paymentMethod: '',
    dateFrom: '',
    dateTo: '',
    sku: '',
    skuFilterType: 'contains_also'
  });
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  
  // 🆕 NEW: Select all functionality
  const [selectAllFiltered, setSelectAllFiltered] = useState(false);
  const [isSelectingAllFiltered, setIsSelectingAllFiltered] = useState(false);
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [trackingInfo, setTrackingInfo] = useState(null);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [syncOptions, setSyncOptions] = useState({
    daysAgo: 1,
    limit: 250,
    includeFulfilled: false
  });
  const [syncLoading, setSyncLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [bulkMenuAnchor, setBulkMenuAnchor] = useState(null);
  const [bulkStatusDialogOpen, setBulkStatusDialogOpen] = useState(false);
  const [bulkStatusValue, setBulkStatusValue] = useState('');
  
  // 🆕 NEW: Import control states
  const [autoImportEnabled, setAutoImportEnabled] = useState(false);
  const [autoTransferEnabled, setAutoTransferEnabled] = useState(false);
  const [importControlsLoading, setImportControlsLoading] = useState(false);
  
  // 🆕 NEW: Fulfillment center management states
  const [fulfillmentCenters, setFulfillmentCenters] = useState([]);
  const [changingFulfillmentCenter, setChangingFulfillmentCenter] = useState(false);
  const [bulkFulfillmentDialogOpen, setBulkFulfillmentDialogOpen] = useState(false);
  const [bulkFulfillmentCenterId, setBulkFulfillmentCenterId] = useState('');

  // SKU management states
  const [availableSkus, setAvailableSkus] = useState([]);
  const [loadingSkus, setLoadingSkus] = useState(false);

  // 🆕 NEW: Tracking sync states
  const [autoTrackingSyncEnabled, setAutoTrackingSyncEnabled] = useState(false);
  const [trackingSyncLoading, setTrackingSyncLoading] = useState(false);
  const [trackingSyncResults, setTrackingSyncResults] = useState(null);
  const [trackingSyncDialogOpen, setTrackingSyncDialogOpen] = useState(false);
  
  // 🆕 NEW: MetaKocka reconciliation states
  const [reconcileResults, setReconcileResults] = useState(null);
  const [reconcileDialogOpen, setReconcileDialogOpen] = useState(false);
  const [reconcileLoading, setReconcileLoading] = useState(false);
  
  // 🆕 NEW: Editable order details states
  const [editMode, setEditMode] = useState(false);
  const [editedOrder, setEditedOrder] = useState(null);
  const [savingOrder, setSavingOrder] = useState(false);

  // 🆕 NEW: Start editing an order
  const handleStartEditing = () => {
    // Prepare line items with pricing fields
    const processedLineItems = selectedOrder.line_items && selectedOrder.line_items.length > 0 
      ? selectedOrder.line_items.map(item => ({
          ...item,
          // Ensure all pricing fields exist
          original_price: item.original_price || item.price || '0.00',
          discount_amount: item.discount_amount || '0.00',
          discount_percentage: item.discount_percentage || '0',
          final_price: item.final_price || item.price || '0.00'
        }))
      : [{
          title: '',
          sku: '',
          quantity: 1,
          original_price: '0.00',
          discount_amount: '0.00',
          discount_percentage: '0',
          final_price: '0.00',
          price: '0.00', // Keep for backward compatibility
          variant_title: ''
        }];

    // 🆕 AUTO-CALCULATE ORDER-LEVEL DISCOUNT
    // Calculate the total of line items (using final_price which includes item-level discounts)
    const lineItemsTotal = processedLineItems.reduce((total, item) => 
      total + (parseFloat(item.final_price || item.price || 0) * parseInt(item.quantity || 1)), 0
    );

    // Get existing shipping and tax amounts (if any)
    const existingShipping = parseFloat(selectedOrder.shipping_amount || 0);
    const existingTax = parseFloat(selectedOrder.tax_amount || 0);

    // Get the actual order total
    const actualOrderTotal = parseFloat(selectedOrder.total_amount || selectedOrder.total_price || 0);
    
    // Calculate order-level discount accounting for shipping/tax:
    // Line Items + Shipping + Tax - Order Discount = Actual Order Total
    // Therefore: Order Discount = Line Items + Shipping + Tax - Actual Order Total
    const orderLevelDiscount = Math.max(0, lineItemsTotal + existingShipping + existingTax - actualOrderTotal);

    console.log('Auto-calculating order discount:', {
      lineItemsTotal: lineItemsTotal.toFixed(2),
      existingShipping: existingShipping.toFixed(2),
      existingTax: existingTax.toFixed(2),
      actualOrderTotal: actualOrderTotal.toFixed(2),
      calculatedDiscount: orderLevelDiscount.toFixed(2)
    });



    setEditedOrder({
      ...selectedOrder,
      // Ensure shipping_address is properly structured
      shipping_address: selectedOrder.shipping_address || {
        name: '',
        address1: '',
        address2: '',
        city: '',
        province: '',
        zip: '',
        country: ''
      },
      // Use processed line items
      line_items: processedLineItems,
      // Order-level pricing with auto-calculated discount
      subtotal: lineItemsTotal.toFixed(2), // Subtotal = sum of line items
      shipping_amount: existingShipping.toFixed(2),
      tax_amount: existingTax.toFixed(2),
      order_discount_amount: orderLevelDiscount > 0 ? orderLevelDiscount.toFixed(2) : (selectedOrder.order_discount_amount || '0.00'),
      total_amount: selectedOrder.total_amount || selectedOrder.total_price || '0.00'
    });
    setEditMode(true);
    
    // Ensure SKUs are loaded for product selection
    if (availableSkus.length === 0 && !loadingSkus) {
      fetchAvailableSkus();
    }
  };

  // 🆕 NEW: Cancel editing
  const handleCancelEdit = () => {
    setEditMode(false);
    setEditedOrder(null);
  };

  // 🆕 NEW: Save edited order
  const handleSaveOrder = async () => {
    if (!isUser()) {
      setError('You do not have permission to edit orders');
      return;
    }

    // Validation: Check if order has at least one line item
    if (!editedOrder.line_items || editedOrder.line_items.length === 0) {
      setError('Order must have at least one product');
      return;
    }

    // Validation: Check for empty required fields
    const hasInvalidItems = editedOrder.line_items.some(item => 
      !item.title?.trim() || 
      !item.sku?.trim() || 
      !item.quantity || 
      parseFloat(item.final_price || item.price || 0) <= 0
    );

    if (hasInvalidItems) {
      setError('All products must have a title, SKU, quantity (>0), and price (>0)');
      return;
    }

    try {
      setSavingOrder(true);
      
      // Calculate the final total amount automatically
      const calculatedSubtotal = editedOrder.line_items.reduce((total, item) => 
        total + (parseFloat(item.final_price || item.price || 0) * parseInt(item.quantity || 1)), 0
      );
      const calculatedTotal = calculatedSubtotal + 
        parseFloat(editedOrder.shipping_amount || 0) + 
        parseFloat(editedOrder.tax_amount || 0) - 
        parseFloat(editedOrder.order_discount_amount || 0);

      const response = await axios.patch(`/api/orders/${selectedOrder.id}`, {
        shipping_address: editedOrder.shipping_address,
        line_items: editedOrder.line_items,
        status: editedOrder.status,
        customer_email: editedOrder.customer_email,
        customer_name: editedOrder.customer_name,
        notes: editedOrder.notes,
        payment_method: editedOrder.payment_method,
        fulfillment_center_id: editedOrder.fulfillment_center_id,
        // Save the calculated total amount
        total_amount: calculatedTotal.toFixed(2)
      });

      if (response.data.success) {
        setSuccess('Order updated successfully');
        setSelectedOrder(response.data.order);
        setEditMode(false);
        setEditedOrder(null);
        fetchOrders(paginationModel); // Refresh the orders list
      }
    } catch (error) {
      console.error('Failed to save order:', error);
      setError('Failed to save order changes');
    } finally {
      setSavingOrder(false);
    }
  };

  // 🆕 NEW: Update shipping address field
  const handleUpdateShippingAddress = (field, value) => {
    setEditedOrder(prev => ({
      ...prev,
      shipping_address: {
        ...prev.shipping_address,
        [field]: value
      }
    }));
  };

  // 🆕 NEW: Update line item with automatic calculations
  const handleUpdateLineItem = (index, field, value) => {
    setEditedOrder(prev => {
      const newLineItems = [...prev.line_items];
      const currentItem = { ...newLineItems[index] };
      
      // Update the specific field
      currentItem[field] = value;
      
      // Auto-calculate prices when relevant fields change
      if (field === 'original_price' || field === 'discount_amount' || field === 'discount_percentage') {
        const originalPrice = parseFloat(currentItem.original_price || 0);
        const discountAmount = parseFloat(currentItem.discount_amount || 0);
        const discountPercentage = parseFloat(currentItem.discount_percentage || 0);
        
        // Calculate final price based on discount type
        let finalPrice = originalPrice;
        
        if (discountPercentage > 0) {
          // Percentage discount takes priority
          finalPrice = originalPrice - (originalPrice * discountPercentage / 100);
          currentItem.discount_amount = (originalPrice * discountPercentage / 100).toFixed(2);
        } else if (discountAmount > 0) {
          // Fixed amount discount
          finalPrice = originalPrice - discountAmount;
          currentItem.discount_percentage = originalPrice > 0 ? ((discountAmount / originalPrice) * 100).toFixed(1) : '0';
        } else {
          // No discount
          currentItem.discount_amount = '0.00';
          currentItem.discount_percentage = '0';
        }
        
        currentItem.final_price = Math.max(0, finalPrice).toFixed(2);
        currentItem.price = currentItem.final_price; // Keep for backward compatibility
      }
      
      // If discount percentage is cleared, recalculate
      if (field === 'discount_percentage' && !value) {
        currentItem.discount_amount = '0.00';
        currentItem.final_price = currentItem.original_price;
        currentItem.price = currentItem.original_price;
      }
      
      // If discount amount is cleared, recalculate
      if (field === 'discount_amount' && !value) {
        currentItem.discount_percentage = '0';
        currentItem.final_price = currentItem.original_price;
        currentItem.price = currentItem.original_price;
      }
      
      newLineItems[index] = currentItem;
      
      // Recalculate totals when line items change
      const newSubtotal = newLineItems.reduce((total, item) => 
        total + (parseFloat(item.final_price || item.price || 0) * parseInt(item.quantity || 1)), 0
      );
      
      return {
        ...prev,
        line_items: newLineItems,
        subtotal: newSubtotal.toFixed(2)
      };
    });
  };

  // 🆕 NEW: Auto-populate product details when SKU is selected
  const handleSkuSelect = async (index, sku) => {
    // Update the SKU first
    handleUpdateLineItem(index, 'sku', sku);
    
    // Try to fetch product details for the SKU
    if (sku && sku.trim()) {
      try {
        const response = await axios.get(`/api/inventory/product/details/${encodeURIComponent(sku)}`);
        if (response.data && response.data.product) {
          const product = response.data.product;
          
          // Auto-populate title and price if available
          if (product.title && !editedOrder.line_items[index]?.title) {
            handleUpdateLineItem(index, 'title', product.title);
          }
          if (product.price && !editedOrder.line_items[index]?.original_price) {
            const price = product.price.toString();
            handleUpdateLineItem(index, 'original_price', price);
            handleUpdateLineItem(index, 'final_price', price);
            handleUpdateLineItem(index, 'price', price); // Backward compatibility
          }
        }
      } catch (error) {
        // Silently fail - user can still manually enter details
        console.debug('Could not fetch product details for SKU:', sku);
      }
    }
    
    // Re-run assignment logic when SKU changes (different products may have different availability)
    setTimeout(() => handleReassignFulfillmentCenter(), 500); // Delay to allow line item updates to complete
  };

  // 🆕 NEW: Add new line item
  const handleAddLineItem = () => {
    setEditedOrder(prev => {
      const newLineItems = [
        ...(prev.line_items || []),
        {
          title: '',
          sku: '',
          quantity: 1,
          original_price: '0.00',
          discount_amount: '0.00',
          discount_percentage: '0',
          final_price: '0.00',
          price: '0.00', // Keep for backward compatibility
          variant_title: ''
        }
      ];
      
      // Recalculate subtotal
      const newSubtotal = newLineItems.reduce((total, item) => 
        total + (parseFloat(item.final_price || item.price || 0) * parseInt(item.quantity || 1)), 0
      );
      
      return {
        ...prev,
        line_items: newLineItems,
        subtotal: newSubtotal.toFixed(2)
      };
    });
  };

  // 🆕 NEW: Remove line item
  const handleRemoveLineItem = (index) => {
    setEditedOrder(prev => {
      const newLineItems = prev.line_items.filter((_, i) => i !== index);
      
      // Recalculate subtotal
      const newSubtotal = newLineItems.reduce((total, item) => 
        total + (parseFloat(item.final_price || item.price || 0) * parseInt(item.quantity || 1)), 0
      );
      
      return {
        ...prev,
        line_items: newLineItems,
        subtotal: newSubtotal.toFixed(2)
      };
    });
    
    // Re-run assignment logic when products change
    handleReassignFulfillmentCenter();
  };

  // 🆕 NEW: Re-assign fulfillment center based on updated order data
  const handleReassignFulfillmentCenter = async (newPaymentMethod) => {
    if (!editedOrder) return;
    
    try {
      // Prepare order data for reassignment
      const orderDataForRules = {
        ...editedOrder,
        payment_method: newPaymentMethod || editedOrder.payment_method,
        line_items: editedOrder.line_items || [],
        shipping_address: editedOrder.shipping_address,
        customer_email: editedOrder.customer_email,
        total_amount: editedOrder.total_amount || selectedOrder.total_amount,
        financial_status: editedOrder.financial_status || selectedOrder.financial_status,
        tags: editedOrder.tags || selectedOrder.tags || []
      };

      console.log('Re-running fulfillment assignment with order data:', orderDataForRules);

      // Call the rules engine to get new assignment
      const response = await axios.post('/api/rules/evaluate', orderDataForRules);
      
      if (response.data.success && response.data.assignment) {
        const newAssignment = response.data.assignment;
        
        // Update the edited order with new fulfillment center
        setEditedOrder(prev => ({
          ...prev,
          fulfillment_center_id: newAssignment.fulfillmentCenter.id,
          fulfillment_centers: newAssignment.fulfillmentCenter
        }));

        // Show notification about the change
        setSuccess(`Fulfillment center updated to: ${newAssignment.fulfillmentCenter.name} (${newAssignment.reason})`);
        
        console.log('Fulfillment center reassigned:', {
          from: selectedOrder.fulfillment_centers?.name || 'Unknown',
          to: newAssignment.fulfillmentCenter.name,
          reason: newAssignment.reason
        });
      }
    } catch (error) {
      console.error('Failed to reassign fulfillment center:', error);
      // Don't show error to user as this is automatic - just log it
    }
  };
  
  // 🆕 UPDATED: Define fetchOrders with pagination support
  const fetchOrders = useCallback(async (currentPaginationModel) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      // 🆕 NEW: Add pagination parameters
      params.append('page', (currentPaginationModel.page + 1).toString()); // DataGrid is 0-indexed, API is 1-indexed
      params.append('limit', currentPaginationModel.pageSize.toString());
      
      // Map frontend filter names to backend parameter names
      const paramMap = {
        search: 'search',
        status: 'status',
        fulfillmentCenter: 'fulfillment_center',
        paymentMethod: 'payment_method',
        dateFrom: 'start_date',
        dateTo: 'end_date',
        sku: 'sku',
        skuFilterType: 'sku_filter_type'
      };
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value && paramMap[key]) {
          params.append(paramMap[key], value);
        }
      });

      const response = await axios.get(`/api/orders?${params.toString()}`);
      
      // 🆕 NEW: Handle paginated response
      const { orders: responseOrders, pagination } = response.data;
      
      // Transform the data for DataGrid
      const transformedOrders = (responseOrders || []).map(order => ({
        id: order.id,
        orderNumber: order.shopify_order_number || order.order_number,
        customerEmail: order.customer_email,
        totalAmount: order.total_price || order.total_amount,
        status: order.status || 'pending', // Use status enum field
        paymentMethod: order.payment_method || 'Unknown', // 💳 NEW: Payment method
        fulfillmentCenter: order.fulfillment_centers?.name || 'Unassigned',
        fulfillmentCenterCode: order.fulfillment_centers?.code || null,
        fulfillmentCenterName: order.fulfillment_centers?.name || 'Unassigned',
        createdAt: order.created_at,
        updatedAt: order.updated_at,
        // ✅ FIX: Show total quantity instead of number of SKUs
        itemCount: order.line_items ? order.line_items.reduce((sum, item) => sum + (item.quantity || 0), 0) : 0,
        shippingAddress: order.shipping_address,
        financialStatus: order.financial_status,
        fulfillmentStatus: order.status, // Use status enum field
        tags: order.tags,
        notes: order.notes
      }));
      
      setOrders(transformedOrders);
      
      // 🆕 NEW: Set total count for pagination
      if (pagination) {
        setTotalOrders(pagination.total);
      }
      
      // Clear any previous errors when successful
      if (error) setError('');
      
    } catch (error) {
      // Error handling - logging now handled by API logger middleware
      
      // More specific error messages
      if (error.response?.status === 401) {
        setError('Unauthorized. Please login again.');
      } else if (error.response?.status === 500) {
        setError('Server error. Please try again later.');
      } else if (error.response?.data?.message) {
        setError(`Failed to load orders: ${error.response.data.message}`);
      } else if (error.code === 'NETWORK_ERROR') {
        setError('Network error. Please check your internet connection.');
      } else {
        setError('Unable to load orders. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [filters, error]); // paginationModel intentionally omitted to prevent infinite loops

  // Debounced search function - create once with useMemo to avoid recreating on every render
  const debouncedFetchOrders = useMemo(
    () => debounce(() => {
      // Reset to first page when filters change
      const resetPaginationModel = { page: 0, pageSize: paginationModel.pageSize };
      setPaginationModel(resetPaginationModel);
      fetchOrders(resetPaginationModel);
    }, 300),
    [fetchOrders, paginationModel.pageSize] // Include pageSize since it's referenced in the function
  );

  // 🆕 NEW: Handle pagination changes
  const handlePaginationModelChange = useCallback((newPaginationModel) => {
    setPaginationModel(newPaginationModel);
    fetchOrders(newPaginationModel);
  }, [fetchOrders]);

  // Auto-trigger search when filters change
  useEffect(() => {
    debouncedFetchOrders();
  }, [filters, debouncedFetchOrders]);

  // 🆕 NEW: Load import settings, fulfillment centers, and SKUs on component mount
  useEffect(() => {
    fetchImportSettings();
    fetchFulfillmentCenters();
    fetchAvailableSkus();
  }, []);

  // 🆕 NEW: Select all filtered orders functionality
  const handleSelectAllFiltered = async () => {
    if (!isUser()) {
      setError('You do not have permission to select orders');
      return;
    }

    try {
      setIsSelectingAllFiltered(true);
      
      // Get all order IDs that match current filters (without pagination)
      const params = new URLSearchParams();
      params.append('page', '1');
      params.append('limit', '9999'); // Get all matching orders
      
      // Apply current filters
      const paramMap = {
        search: 'search',
        status: 'status',
        fulfillmentCenter: 'fulfillment_center',
        paymentMethod: 'payment_method', // 🔧 FIXED: Added payment method mapping
        dateFrom: 'start_date',
        dateTo: 'end_date',
        sku: 'sku',
        skuFilterType: 'sku_filter_type'
      };
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value && paramMap[key]) {
          params.append(paramMap[key], value);
        }
      });

      const response = await axios.get(`/api/orders?${params.toString()}`);
      const allFilteredOrderIds = response.data.orders.map(order => order.id);
      
      setSelectedRows(allFilteredOrderIds);
      setSelectAllFiltered(true);
      setSuccess(`Selected ${allFilteredOrderIds.length} filtered orders across all pages`);
      
    } catch (error) {
      console.error('Failed to select all filtered orders:', error);
      setError('Failed to select all filtered orders');
    } finally {
      setIsSelectingAllFiltered(false);
    }
  };

  // 🆕 NEW: Clear selection
  const handleClearSelection = () => {
    setSelectedRows([]);
    setSelectAllFiltered(false);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      status: '',
      fulfillmentCenter: '',
      paymentMethod: '',
      dateFrom: '',
      dateTo: '',
      sku: '',
      skuFilterType: 'contains_also'
    });
    
    // 🆕 NEW: Reset pagination and selection when clearing filters
    const resetPaginationModel = { page: 0, pageSize: paginationModel.pageSize };
    setPaginationModel(resetPaginationModel);
    setSelectedRows([]);
    setSelectAllFiltered(false);
    
    setTimeout(() => fetchOrders(resetPaginationModel), 100);
  };

  const handleOrderClick = async (orderId) => {
    try {
      const response = await axios.get(`/api/orders/${orderId}`);
      setSelectedOrder(response.data.order);
      setDialogOpen(true);
      
      // Fetch enhanced tracking information (EliZ refresh + Poste Italiane)
      await fetchEnhancedTracking(orderId);
    } catch (error) {
      // Error logging handled by API logger
      
      if (error.response?.status === 404) {
        setError('Order not found.');
      } else if (error.response?.data?.message) {
        setError(`Failed to load order details: ${error.response.data.message}`);
      } else {
        setError('Unable to load order details.');
      }
    }
  };

  const fetchEnhancedTracking = async (orderId) => {
    try {
      setTrackingLoading(true);
      const response = await axios.get(`/api/orders/${orderId}/tracking/enhanced`);
      setTrackingInfo(response.data);
      
      // Show success message if tracking was updated
      if (response.data.updated_tracking) {
        setSuccess('Tracking information updated from fulfillment center');
      }
    } catch (error) {
      console.error('Failed to fetch enhanced tracking:', error);
      setTrackingInfo(null);
    } finally {
      setTrackingLoading(false);
    }
  };

  const handleRetryOrder = async (orderId) => {
    if (!isUser()) {
      setError('You do not have permission to retry orders');
      return;
    }

    try {
      await axios.post(`/api/orders/${orderId}/retry`);
      setSuccess('Order retry initiated');
      fetchOrders(paginationModel);
    } catch (error) {
      console.error('Failed to retry order:', error);
      setError('Failed to retry order');
    }
  };

  const handleCancelOrder = async (orderId) => {
    if (!isUser()) {
      setError('You do not have permission to cancel orders');
      return;
    }

    try {
      await axios.post(`/api/orders/${orderId}/cancel`);
      setSuccess('Order cancelled successfully');
      fetchOrders(paginationModel);
    } catch (error) {
      console.error('Failed to cancel order:', error);
      setError('Failed to cancel order');
    }
  };

  const handleManualSync = async () => {
    if (!isUser()) {
      setError('You do not have permission to sync orders');
      return;
    }

    try {
      setSyncLoading(true);
      
      // Calculate sinceDate from daysAgo
      const daysAgo = parseInt(syncOptions.daysAgo) || 1;
      const sinceDate = new Date(Date.now() - (daysAgo * 24 * 60 * 60 * 1000)).toISOString();
      
      const payload = {
        sinceDate,
        limit: parseInt(syncOptions.limit),
        includeFulfilled: syncOptions.includeFulfilled
      };

      console.log('🔧 Manual sync payload:', payload);

      const response = await axios.post('/api/orders/sync', payload);
      
      // Enhanced success message with proper formatting
      const newOrders = response.data.stats?.newOrders || 0;
      const updatedOrders = response.data.stats?.updatedOrders || 0;
      const rejectedOrders = response.data.stats?.rejectedOrders || 0;
      const totalProcessed = newOrders + updatedOrders + rejectedOrders;
      
      // Show time range in success message
      const timeRangeText = `${daysAgo} day${daysAgo !== 1 ? 's' : ''}`;
      
      if (totalProcessed === 0) {
        setSuccess(`Sync completed for last ${timeRangeText}. No new orders found.`);
      } else {
        setSuccess(
          `Sync completed for last ${timeRangeText}! ${newOrders} new orders, ${updatedOrders} updated, ${rejectedOrders} rejected by import rules.`
        );
      }
      
      setSyncDialogOpen(false);
      fetchOrders(paginationModel);
    } catch (error) {
      // Error logging handled by API logger
      
      // Better error handling for sync
      if (error.response?.status === 429) {
        setError('Too many sync attempts. Please try again in a few minutes.');
      } else if (error.response?.data?.message) {
        setError(`Sync failed: ${error.response.data.message}`);
      } else if (error.code === 'NETWORK_ERROR') {
        setError('Network error during sync.');
      } else {
        setError('Sync failed. Please check your Shopify settings.');
      }
    } finally {
      setSyncLoading(false);
    }
  };

  // 🆕 NEW: Manual tracking sync
  const handleManualTrackingSync = async () => {
    if (!isUser()) {
      setError('You do not have permission to sync tracking information');
      return;
    }

    try {
      setTrackingSyncLoading(true);
      setTrackingSyncResults(null);
      
      const response = await axios.post('/api/orders/tracking/sync');
      
      const stats = response.data.stats || {};
      const updatedCount = stats.totalUpdated || stats.synced || 0;
      const checkedCount = stats.totalChecked || stats.checked || 0;
      const details = stats.details || [];
      
      setTrackingSyncResults({
        success: true,
        updatedCount,
        checkedCount,
        details,
        message: response.data.message
      });

      if (updatedCount > 0) {
        setSuccess(`Tracking sync completed! ${updatedCount} orders updated with new tracking numbers.`);
        fetchOrders(paginationModel); // Refresh the orders list
      } else {
        setSuccess('Tracking sync completed. No new tracking information found.');
      }
      
      // Show results dialog
      setTrackingSyncDialogOpen(true);
      
    } catch (error) {
      console.error('Failed to sync tracking:', error);
      setTrackingSyncResults({
        success: false,
        message: error.response?.data?.message || 'Failed to sync tracking information'
      });
      setError('Failed to sync tracking information');
      setTrackingSyncDialogOpen(true);
    } finally {
      setTrackingSyncLoading(false);
    }
  };

  // 🆕 NEW: MetaKocka reconciliation
  const handleMetaKockaReconcile = async () => {
    if (!isUser()) {
      setError('You do not have permission to reconcile orders');
      return;
    }

    try {
      setReconcileLoading(true);
      setReconcileResults(null);
      
      const response = await axios.post('/api/orders/metakocka/reconcile');
      
      const stats = response.data.stats || {};
      const foundCount = stats.found_in_metakocka || 0;
      const checkedCount = stats.total_checked || 0;
      const updatedCount = stats.updated_in_database || 0;
      const orders = stats.orders || [];
      
      setReconcileResults({
        success: true,
        foundCount,
        checkedCount,
        updatedCount,
        orders: orders.filter(o => o.found_in_metakocka),
        message: response.data.message
      });

      if (updatedCount > 0) {
        setSuccess(`MetaKocka reconciliation completed! ${updatedCount} orders updated and assigned to MetaKocka.`);
        fetchOrders(paginationModel); // Refresh the orders list
      } else if (foundCount > 0) {
        setSuccess(`MetaKocka reconciliation completed. ${foundCount} orders found but already up to date.`);
      } else {
        setSuccess('MetaKocka reconciliation completed. No orders found in MetaKocka.');
      }
      
      // Show results dialog
      setReconcileDialogOpen(true);
      
    } catch (error) {
      console.error('Failed to reconcile with MetaKocka:', error);
      setReconcileResults({
        success: false,
        message: error.response?.data?.message || 'Failed to reconcile with MetaKocka'
      });
      setError('Failed to reconcile with MetaKocka');
      setReconcileDialogOpen(true);
    } finally {
      setReconcileLoading(false);
    }
  };

  // 🔧 AUTOMATIC: Fix status inconsistencies (background operation)
  const fixStatusInconsistencies = async (silent = true) => {
    try {
      const response = await axios.post('/api/orders/fix-status-inconsistencies', {
        limit: 100,
        dry_run: false
      });
      
      const stats = response.data.stats || {};
      const fixedCount = stats.fixed || 0;
      
      if (!silent && fixedCount > 0) {
        setSuccess(`Automatically fixed ${fixedCount} orders with status inconsistencies.`);
      }
      
      return { success: true, fixedCount };
      
    } catch (error) {
      console.error('Background status fix failed:', error);
      return { success: false, error: error.message };
    }
  };

  // 🔧 ENHANCED: Refresh orders with automatic status fix
  const handleRefreshOrders = async () => {
    try {
      // First, fix any status inconsistencies quietly
      await fixStatusInconsistencies(true);
      
      // Then refresh the orders list
      await fetchOrders(paginationModel);
      
    } catch (error) {
      console.error('Error during refresh:', error);
      // Even if status fix fails, still try to refresh orders
      await fetchOrders(paginationModel);
    }
  };

  // 📥 ENHANCED: CSV Export functionality with better payment method filtering support
  const handleExportCSV = async () => {
    try {
      let ordersToExport = orders;
      let exportCount = orders.length;
      let exportType = '';
      let filename = `orders-export-${new Date().toISOString().split('T')[0]}.csv`;
      
      const hasFilters = Object.values(filters).some(value => value && value.trim && value.trim() !== '');
      const hasSelection = selectedRows.length > 0;
      
      // 🔧 ENHANCED: Better parameter mapping for all filters including payment method
      const buildFilterParams = () => {
        const params = new URLSearchParams();
        
        // Complete parameter mapping - ensure all filters are properly mapped
        const paramMap = {
          search: 'search',
          status: 'status',
          fulfillmentCenter: 'fulfillment_center',
          paymentMethod: 'payment_method', // 🔧 CRITICAL: Ensure payment method is included
          dateFrom: 'start_date',
          dateTo: 'end_date',
          sku: 'sku',
          skuFilterType: 'sku_filter_type'
        };
        
        // Apply all current filters
        Object.entries(filters).forEach(([key, value]) => {
          if (value && value.toString().trim() !== '' && paramMap[key]) {
            params.append(paramMap[key], value);
            console.log(`Export filter applied: ${paramMap[key]} = ${value}`); // Debug logging
          }
        });
        
        return params;
      };
      
      // 🆕 NEW: Priority order for export logic
      if (hasSelection) {
        console.log(`Exporting ${selectedRows.length} selected orders...`);
        
        // 1. HIGHEST PRIORITY: Export selected orders
        if (selectAllFiltered) {
          // If "select all filtered" was used, get all filtered orders
          const params = buildFilterParams();
          params.append('page', '1');
          params.append('limit', '9999');
          
          console.log('Fetching all filtered orders for export:', params.toString());
          
          const response = await axios.get(`/api/orders?${params.toString()}`);
          ordersToExport = (response.data.orders || []).map(order => transformOrderForExport(order));
          exportType = 'all filtered ';
          filename = `orders-selected-filtered-export-${new Date().toISOString().split('T')[0]}.csv`;
        } else {
          // Export only the specifically selected orders from current view
          ordersToExport = orders.filter(order => selectedRows.includes(order.id));
          exportType = 'selected ';
          filename = `orders-selected-export-${new Date().toISOString().split('T')[0]}.csv`;
        }
        exportCount = ordersToExport.length;
        
      } else if (hasFilters) {
        console.log('Exporting all filtered orders...', filters);
        
        // 2. MEDIUM PRIORITY: Export all filtered orders (if no selection)
        const params = buildFilterParams();
        params.append('page', '1');
        params.append('limit', '9999');
        
        console.log('Fetching filtered orders for export:', params.toString());

        const response = await axios.get(`/api/orders?${params.toString()}`);
        ordersToExport = (response.data.orders || []).map(order => transformOrderForExport(order));
        exportType = 'filtered ';
        filename = `orders-filtered-export-${new Date().toISOString().split('T')[0]}.csv`;
        exportCount = ordersToExport.length;
        
      } else {
        console.log('Exporting all orders...');
        
        // 3. LOWEST PRIORITY: Export ALL orders (if no selection and no filters)
        const params = new URLSearchParams();
        params.append('page', '1');
        params.append('limit', '9999'); // Get all orders
        
        const response = await axios.get(`/api/orders?${params.toString()}`);
        ordersToExport = (response.data.orders || []).map(order => transformOrderForExport(order));
        exportType = 'all ';
        filename = `orders-all-export-${new Date().toISOString().split('T')[0]}.csv`;
        exportCount = ordersToExport.length;
      }
      
      console.log(`Export prepared: ${exportCount} ${exportType}orders`);
      
      // Convert orders data to CSV format
      const csvData = convertOrdersToCSV(ordersToExport);
      
      // Create and trigger download
      downloadCSV(csvData, filename);
      
      setSuccess(`Successfully exported ${exportCount} ${exportType}orders to CSV`);
    } catch (error) {
      console.error('Failed to export CSV:', error);
      setError(`Failed to export orders to CSV: ${error.message}`);
    }
  };
  
  // Helper function to transform order data for export
  const transformOrderForExport = (order) => ({
    id: order.id,
    orderNumber: order.shopify_order_number || order.order_number,
    customerEmail: order.customer_email,
    totalAmount: order.total_price || order.total_amount,
    status: order.status || 'pending', // Use status enum field
    paymentMethod: order.payment_method || 'Unknown',
    fulfillmentCenter: order.fulfillment_centers?.name || 'Unassigned',
    fulfillmentCenterCode: order.fulfillment_centers?.code || null,
    fulfillmentCenterName: order.fulfillment_centers?.name || 'Unassigned',
    createdAt: order.created_at,
    updatedAt: order.updated_at,
    itemCount: order.line_items ? order.line_items.reduce((sum, item) => sum + (item.quantity || 0), 0) : 0,
    shippingAddress: order.shipping_address,
    financialStatus: order.financial_status,
    fulfillmentStatus: order.status, // Use status enum field
    tags: order.tags,
    notes: order.notes
  });

  // Helper function to convert orders data to CSV format
  const convertOrdersToCSV = (ordersData) => {
    if (!ordersData || ordersData.length === 0) {
      return 'No data to export';
    }

    // Define CSV headers
    const headers = [
      'Order Number',
      'Customer Email',
      'Total Amount (EUR)',
      'Payment Method',
      'Fulfillment Status',
      'Fulfillment Center',
      'Item Count',
      'Financial Status',
      'Tags',
      'Created Date',
      'Updated Date',
      'Shipping Address Name',
      'Shipping Address Line 1',
      'Shipping Address Line 2', 
      'Shipping City',
      'Shipping Province',
      'Shipping Postal Code',
      'Shipping Country',
      'Notes'
    ];

    // Convert orders to CSV rows
    const csvRows = ordersData.map(order => {
      return [
        `"${order.orderNumber || ''}"`,
        `"${order.customerEmail || ''}"`,
        `"${parseFloat(order.totalAmount || 0).toFixed(2)}"`,
        `"${order.paymentMethod || 'Unknown'}"`,
        `"${order.fulfillmentStatus || order.status || 'Pending'}"`,
        `"${order.fulfillmentCenter || 'Unassigned'}"`,
        `"${order.itemCount || 0}"`,
        `"${order.financialStatus || ''}"`,
        `"${order.tags || ''}"`,
        `"${order.createdAt ? new Date(order.createdAt).toLocaleString('en-GB') : ''}"`,
        `"${order.updatedAt ? new Date(order.updatedAt).toLocaleString('en-GB') : ''}"`,
        `"${order.shippingAddress?.name || ''}"`,
        `"${order.shippingAddress?.address1 || ''}"`,
        `"${order.shippingAddress?.address2 || ''}"`,
        `"${order.shippingAddress?.city || ''}"`,
        `"${order.shippingAddress?.province || ''}"`,
        `"${order.shippingAddress?.zip || ''}"`,
        `"${order.shippingAddress?.country || ''}"`,
        `"${order.notes || ''}"`
      ].join(',');
    });

    // Combine headers and rows
    return [headers.join(','), ...csvRows].join('\n');
  };

  // Helper function to download CSV file
  const downloadCSV = (csvContent, filename) => {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Bulk Actions
  const handleBulkMenuOpen = (event) => {
    setBulkMenuAnchor(event.currentTarget);
  };

  const handleBulkMenuClose = () => {
    setBulkMenuAnchor(null);
  };

  const handleBulkDelete = async () => {
    if (!isUser()) {
      setError('You do not have permission to delete orders');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedRows.length} orders? This action cannot be undone.`)) {
      return;
    }

    try {
      await axios.delete('/api/orders/bulk', {
        data: { orderIds: selectedRows }
      });
      
      setSuccess(`Successfully deleted ${selectedRows.length} orders`);
      setSelectedRows([]);
      setSelectAllFiltered(false);
      fetchOrders(paginationModel);
    } catch (error) {
      console.error('Failed to delete orders:', error);
      setError(error.response?.data?.message || 'Failed to delete orders');
    }
    handleBulkMenuClose();
  };

  const handleBulkStatusUpdate = async () => {
    if (!isUser()) {
      setError('You do not have permission to update order status');
      return;
    }

    if (!bulkStatusValue) {
      setError('Please select a status');
      return;
    }

    try {
      await axios.patch('/api/orders/bulk/status', {
        orderIds: selectedRows,
        status: bulkStatusValue
      });
      
      setSuccess(`Successfully updated status for ${selectedRows.length} orders`);
      setSelectedRows([]);
      setSelectAllFiltered(false);
      setBulkStatusDialogOpen(false);
      setBulkStatusValue('');
      fetchOrders(paginationModel);
    } catch (error) {
      console.error('Failed to update order status:', error);
      setError(error.response?.data?.message || 'Failed to update order status');
    }
  };

  const handleBulkCancel = async () => {
    if (!isUser()) {
      setError('You do not have permission to cancel orders');
      return;
    }

    if (!window.confirm(`Are you sure you want to cancel ${selectedRows.length} orders?`)) {
      return;
    }

    try {
      await axios.post('/api/orders/bulk/cancel', {
        orderIds: selectedRows
      });
      
      setSuccess(`Successfully cancelled ${selectedRows.length} orders`);
      setSelectedRows([]);
      setSelectAllFiltered(false);
      fetchOrders(paginationModel);
    } catch (error) {
      console.error('Failed to cancel orders:', error);
      setError(error.response?.data?.message || 'Failed to cancel orders');
    }
    handleBulkMenuClose();
  };

  // 🆕 NEW: Handle fulfillment center change
  const handleChangeFulfillmentCenter = async (orderId, newFulfillmentCenterId) => {
    if (!isUser()) {
      setError('You do not have permission to change fulfillment centers');
      return;
    }

    try {
      setChangingFulfillmentCenter(true);
      
      await axios.patch(`/api/orders/${orderId}/fulfillment-center`, {
        fulfillment_center_id: newFulfillmentCenterId
      });
      
      setSuccess('Fulfillment center updated successfully');
      
      // Update the selected order if it's the one being changed
      if (selectedOrder && selectedOrder.id === orderId) {
        const updatedOrderResponse = await axios.get(`/api/orders/${orderId}`);
        setSelectedOrder(updatedOrderResponse.data.order);
      }
      
      // Refresh orders list
      fetchOrders(paginationModel);
      
    } catch (error) {
      console.error('Failed to change fulfillment center:', error);
      setError(error.response?.data?.error || 'Failed to change fulfillment center');
    } finally {
      setChangingFulfillmentCenter(false);
    }
  };

  // 🆕 NEW: Handle bulk fulfillment center change
  const handleBulkFulfillmentChange = async () => {
    if (!isUser()) {
      setError('You do not have permission to change fulfillment centers');
      return;
    }

    if (selectedRows.length === 0) {
      setError('Please select orders to update');
      return;
    }

    if (!bulkFulfillmentCenterId) {
      setError('Please select a fulfillment center');
      return;
    }

    try {
      setChangingFulfillmentCenter(true);
      
      // 🔧 Add logging for debugging
      console.log('Bulk FC change:', {
        orderIds: selectedRows,
        fulfillment_center_id: bulkFulfillmentCenterId,
        orderCount: selectedRows.length
      });
      
      const response = await axios.patch('/api/orders/bulk/fulfillment-center', {
        orderIds: selectedRows,
        fulfillment_center_id: bulkFulfillmentCenterId
      });
      
      console.log('Bulk FC change response:', response.data);
      
      const centerName = fulfillmentCenters.find(fc => fc.id === bulkFulfillmentCenterId)?.name || 'Unknown';
      setSuccess(`Successfully updated fulfillment center to ${centerName} for ${selectedRows.length} order(s)`);
      setSelectedRows([]);
      setSelectAllFiltered(false);
      setBulkFulfillmentDialogOpen(false);
      setBulkFulfillmentCenterId('');
      fetchOrders(paginationModel);
      
    } catch (error) {
      console.error('Bulk FC change failed:', {
        error: error,
        response: error.response?.data,
        status: error.response?.status,
        orderIds: selectedRows,
        fulfillment_center_id: bulkFulfillmentCenterId
      });
      
      // 🔧 Improved error handling
      const errorMessage = error.response?.data?.error || error.message || 'Failed to update fulfillment centers';
      setError(`Bulk fulfillment center update failed: ${errorMessage}`);
    } finally {
      setChangingFulfillmentCenter(false);
    }
  };

  const columns = [
    {
      field: 'orderNumber',
      headerName: 'Order #',
      width: 130,
      renderCell: (params) => {
        // Get status-based color for the order number
        const getOrderNumberColor = (status) => {
          switch (status?.toLowerCase()) {
            case 'sent':
              return '#3b82f6'; // Blue for sent
            case 'fulfilled':
              return '#3b82f6'; // Blue for fulfilled
            case 'shipped':
            case 'delivered':
              return '#22c55e'; // Green for delivered
            case 'problem':
            case 'cancelled':
            case 'failed':
              return '#ef4444'; // Red for failed/problem
            case 'pending':
              return '#f59e0b'; // Orange for pending
            default:
              return '#6366f1'; // Default blue
          }
        };

        return (
          <Button
            variant="text"
            onClick={() => handleOrderClick(params.row.id)}
            sx={{ 
              textTransform: 'none',
              color: getOrderNumberColor(params.row.status),
              fontWeight: 600,
              '&:hover': {
                backgroundColor: `${getOrderNumberColor(params.row.status)}15`,
              }
            }}
          >
            #{params.value}
          </Button>
        );
      }
    },
    {
      field: 'customerEmail',
      headerName: 'Customer',
      width: 200,
      renderCell: (params) => {
        // Add color coding based on order value
        const getCustomerDisplayColor = (amount) => {
          const value = parseFloat(amount || 0);
          if (value >= 100) {
            return {
              color: '#22c55e', // Green for high value
              fontWeight: 600
            };
          } else if (value >= 50) {
            return {
              color: '#3b82f6', // Blue for medium value
              fontWeight: 500
            };
          } else {
            return {
              color: 'inherit',
              fontWeight: 400
            };
          }
        };

        const styles = getCustomerDisplayColor(params.row.totalAmount);
        
        return (
          <Typography
            variant="body2"
            sx={{
              ...styles,
              fontSize: '0.875rem'
            }}
          >
            {params.value}
          </Typography>
        );
      }
    },
    {
      field: 'totalAmount',
      headerName: 'Amount',
      width: 120,
      renderCell: (params) => {
        const amount = parseFloat(params.value || 0);
        
        // Color coding based on amount ranges
        const getAmountColor = (amount) => {
          if (amount >= 100) {
            return {
              backgroundColor: 'rgba(34, 197, 94, 0.15)',
              color: '#22c55e',
              borderColor: 'rgba(34, 197, 94, 0.3)'
            };
          } else if (amount >= 50) {
            return {
              backgroundColor: 'rgba(59, 130, 246, 0.15)',
              color: '#3b82f6',
              borderColor: 'rgba(59, 130, 246, 0.3)'
            };
          } else if (amount >= 25) {
            return {
              backgroundColor: 'rgba(245, 158, 11, 0.15)',
              color: '#f59e0b',
              borderColor: 'rgba(245, 158, 11, 0.3)'
            };
          } else {
            return {
              backgroundColor: 'rgba(156, 163, 175, 0.15)',
              color: '#9ca3af',
              borderColor: 'rgba(156, 163, 175, 0.3)'
            };
          }
        };

        const styles = getAmountColor(amount);
        
        return (
          <Chip
            label={`€${amount.toFixed(2)}`}
            variant="outlined"
            size="small"
            sx={{
              ...styles,
              fontWeight: 700,
              fontSize: '0.75rem',
              borderWidth: '1px',
              '& .MuiChip-label': {
                px: 1.5
              }
            }}
          />
        );
      }
    },
    {
      field: 'paymentMethod',
      headerName: 'Payment',
      width: 140,
      renderCell: (params) => {
        const paymentMethod = params.value;
        const getPaymentMethodColors = (method) => {
          if (!method || method === 'Unknown') {
            return {
              backgroundColor: 'rgba(156, 163, 175, 0.15)',
              color: '#9ca3af',
              borderColor: 'rgba(156, 163, 175, 0.3)'
            };
          }
          
          if (method.includes('Contrassegno') || method.toLowerCase().includes('cod')) {
            return {
              backgroundColor: 'rgba(245, 158, 11, 0.15)',
              color: '#fbbf24',
              borderColor: 'rgba(245, 158, 11, 0.3)'
            };
          }
          
          if (method.toLowerCase().includes('shopify_payments') || method.toLowerCase().includes('stripe')) {
            return {
              backgroundColor: 'rgba(34, 197, 94, 0.15)',
              color: '#4ade80',
              borderColor: 'rgba(34, 197, 94, 0.3)'
            };
          }
          
          if (method.toLowerCase().includes('paypal')) {
            return {
              backgroundColor: 'rgba(59, 130, 246, 0.15)',
              color: '#60a5fa',
              borderColor: 'rgba(59, 130, 246, 0.3)'
            };
          }
          
          return {
            backgroundColor: 'rgba(168, 85, 247, 0.15)',
            color: '#c4b5fd',
            borderColor: 'rgba(168, 85, 247, 0.3)'
          };
        };
        
        const colors = getPaymentMethodColors(paymentMethod);
        return (
          <Chip
            label={paymentMethod || 'Unknown'}
            variant="outlined"
            size="small"
            sx={{
              ...colors,
              fontSize: '0.7rem',
              fontWeight: 600,
              borderWidth: '1px',
              '& .MuiChip-label': {
                px: 1.5
              }
            }}
          />
        );
      }
    },
    {
      field: 'fulfillmentStatus',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => {
        const status = params.value?.toLowerCase();
        
        // Define custom colors for each status
        const getStatusStyles = (status) => {
          switch (status) {
            case 'pending':
            case 'unknown':
              return {
                backgroundColor: 'rgba(245, 158, 11, 0.15)',
                color: '#f59e0b',
                borderColor: 'rgba(245, 158, 11, 0.3)'
              };
            case 'problem':
              return {
                backgroundColor: 'rgba(239, 68, 68, 0.15)',
                color: '#ef4444',
                borderColor: 'rgba(239, 68, 68, 0.3)'
              };
            case 'sent':
              return {
                backgroundColor: 'rgba(59, 130, 246, 0.15)',
                color: '#3b82f6',
                borderColor: 'rgba(59, 130, 246, 0.3)'
              };
            case 'fulfilled':
              return {
                backgroundColor: 'rgba(59, 130, 246, 0.15)',
                color: '#3b82f6',
                borderColor: 'rgba(59, 130, 246, 0.3)'
              };
            case 'shipped':
            case 'delivered':
            case 'paid':
              return {
                backgroundColor: 'rgba(34, 197, 94, 0.15)',
                color: '#22c55e',
                borderColor: 'rgba(34, 197, 94, 0.3)'
              };
            case 'hold':
              return {
                backgroundColor: 'rgba(251, 146, 60, 0.15)',
                color: '#fb923c',
                borderColor: 'rgba(251, 146, 60, 0.3)'
              };
            case 'cancelled':
            case 'failed':
            case 'refunded':
              return {
                backgroundColor: 'rgba(239, 68, 68, 0.15)',
                color: '#ef4444',
                borderColor: 'rgba(239, 68, 68, 0.3)'
              };
            case 'processing':
              return {
                backgroundColor: 'rgba(168, 85, 247, 0.15)',
                color: '#a855f7',
                borderColor: 'rgba(168, 85, 247, 0.3)'
              };
            default:
              return {
                backgroundColor: 'rgba(156, 163, 175, 0.15)',
                color: '#9ca3af',
                borderColor: 'rgba(156, 163, 175, 0.3)'
              };
          }
        };
        
        const styles = getStatusStyles(status);
        
        // Ensure proper status display - map to display text
        const getDisplayStatus = (status) => {
          if (!status) return 'Pending';
          
          // Handle the status properly regardless of case
          const lowerStatus = status.toLowerCase();
          switch (lowerStatus) {
            case 'sent':
              return 'Sent';
            case 'pending':
            case 'unknown':
              return 'Pending';
            case 'problem':
              return 'Problem';
            case 'shipped':
              return 'Shipped';
            case 'delivered':
              return 'Delivered';
            case 'fulfilled':
              return 'Fulfilled';
            case 'cancelled':
              return 'Cancelled';
            case 'failed':
              return 'Failed';
            case 'refunded':
              return 'Refunded';
            case 'processing':
              return 'Processing';
            default:
              return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
          }
        };
        
        return (
          <Chip
            label={getDisplayStatus(params.value)}
            variant="outlined"
            size="small"
            className={`status-${status}`}
            sx={{
              ...styles,
              fontWeight: 700,
              fontSize: '0.75rem',
              borderWidth: '1.5px',
              '& .MuiChip-label': {
                px: 1.5,
                py: 0.5
              }
            }}
          />
        );
      }
    },
    {
      field: 'fulfillmentCenter',
      headerName: 'Fulfillment Center',
      width: 150,
      renderCell: (params) => {
        const centerName = params.value;
        const getColorForCenter = (name) => {
          if (!name || name === 'Unassigned') {
            return {
              backgroundColor: 'rgba(239, 68, 68, 0.15)',
              color: '#fca5a5',
              borderColor: 'rgba(239, 68, 68, 0.3)'
            };
          }
          switch (name.toLowerCase()) {
            case 'metakocka':
              return {
                backgroundColor: 'rgba(34, 197, 94, 0.15)',
                color: '#4ade80',
                borderColor: 'rgba(34, 197, 94, 0.3)'
              };
            case 'elizetaplus':
            case 'eliz':
              return {
                backgroundColor: 'rgba(99, 102, 241, 0.15)',
                color: '#a5b4fc',
                borderColor: 'rgba(99, 102, 241, 0.3)'
              };
            default:
              return {
                backgroundColor: 'rgba(168, 85, 247, 0.15)',
                color: '#c4b5fd',
                borderColor: 'rgba(168, 85, 247, 0.3)'
              };
          }
        };
        
        const colors = getColorForCenter(centerName);
        return (
          <Chip
            label={centerName || 'Unassigned'}
            variant="outlined"
            size="small"
            sx={{
              ...colors,
              fontWeight: 600,
              fontSize: '0.75rem',
              borderWidth: '1px',
              '& .MuiChip-label': {
                px: 1.5
              }
            }}
          />
        );
      }
    },
    {
      field: 'itemCount',
      headerName: 'Items',
      width: 90,
      renderCell: (params) => {
        const itemCount = params.value || 0;
        
        // Color coding based on item count
        const getItemCountColor = (count) => {
          if (count >= 10) {
            return {
              backgroundColor: 'rgba(168, 85, 247, 0.15)',
              color: '#a855f7',
              borderColor: 'rgba(168, 85, 247, 0.3)'
            };
          } else if (count >= 5) {
            return {
              backgroundColor: 'rgba(34, 197, 94, 0.15)',
              color: '#22c55e',
              borderColor: 'rgba(34, 197, 94, 0.3)'
            };
          } else if (count >= 2) {
            return {
              backgroundColor: 'rgba(59, 130, 246, 0.15)',
              color: '#3b82f6',
              borderColor: 'rgba(59, 130, 246, 0.3)'
            };
          } else {
            return {
              backgroundColor: 'rgba(245, 158, 11, 0.15)',
              color: '#f59e0b',
              borderColor: 'rgba(245, 158, 11, 0.3)'
            };
          }
        };

        const styles = getItemCountColor(itemCount);
        
        return (
          <Chip
            label={itemCount}
            variant="outlined"
            size="small"
            sx={{
              ...styles,
              fontWeight: 600,
              fontSize: '0.75rem',
              borderWidth: '1px',
              minWidth: '35px',
              '& .MuiChip-label': {
                px: 1
              }
            }}
          />
        );
      }
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      width: 160,
      renderCell: (params) => {
        const date = new Date(params.value);
        const now = new Date();
        const diffInHours = (now - date) / (1000 * 60 * 60);
        
        // Color coding based on order age
        const getDateColor = (hoursOld) => {
          if (hoursOld < 1) {
            return {
              color: '#22c55e', // Green for very recent (< 1 hour)
              fontWeight: 600
            };
          } else if (hoursOld < 24) {
            return {
              color: '#3b82f6', // Blue for recent (< 1 day)
              fontWeight: 500
            };
          } else if (hoursOld < 168) { // 7 days
            return {
              color: '#f59e0b', // Orange for this week
              fontWeight: 400
            };
          } else {
            return {
              color: '#9ca3af', // Gray for older
              fontWeight: 400
            };
          }
        };

        const styles = getDateColor(diffInHours);
        
        // Get relative time text
        const getRelativeTime = (hoursOld) => {
          if (hoursOld < 1) {
            const minutes = Math.floor(hoursOld * 60);
            return `${minutes}m ago`;
          } else if (hoursOld < 24) {
            return `${Math.floor(hoursOld)}h ago`;
          } else if (hoursOld < 168) {
            return `${Math.floor(hoursOld / 24)}d ago`;
          } else {
            return `${Math.floor(hoursOld / 168)}w ago`;
          }
        };

        return (
          <Box>
            <Typography
              variant="body2"
              sx={{
                ...styles,
                fontSize: '0.875rem'
              }}
            >
              {getRelativeTime(diffInHours)}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'rgba(255,255,255,0.6)',
                fontSize: '0.7rem',
                display: 'block'
              }}
            >
              {date.toLocaleDateString('it-IT', {
                day: '2-digit',
                month: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </Typography>
          </Box>
        );
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Button
            size="small"
            startIcon={<Visibility />}
            onClick={() => handleOrderClick(params.row.id)}
          >
            View
          </Button>
          {isUser() && params.row.status === 'failed' && (
            <Button
              size="small"
              startIcon={<RestartAlt />}
              onClick={() => handleRetryOrder(params.row.id)}
              color="warning"
            >
              Retry
            </Button>
          )}
        </Box>
      )
    }
  ];

  // 🆕 NEW: Fetch import control settings
  const fetchImportSettings = async () => {
    try {
      const response = await axios.get('/api/settings/import/status');
      setAutoImportEnabled(response.data.autoImportEnabled);
      setAutoTransferEnabled(response.data.autoTransferEnabled);
      setAutoTrackingSyncEnabled(response.data.autoTrackingSyncEnabled);
    } catch (error) {
      console.error('Failed to fetch import settings:', error);
    }
  };

  // 🆕 NEW: Fetch fulfillment centers for order management
  const fetchFulfillmentCenters = async () => {
    try {
      const response = await axios.get('/api/fulfillment/centers');
      // ✅ FIX: API returns { data: centers } not { fulfillmentCenters: centers }
      setFulfillmentCenters(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch fulfillment centers:', error);
    }
  };

  // 🆕 NEW: Fetch available SKUs for filtering
  const fetchAvailableSkus = async () => {
    try {
      setLoadingSkus(true);
      const response = await axios.get('/api/orders/skus');
      setAvailableSkus(response.data.skus || []);
    } catch (error) {
      console.error('Failed to fetch available SKUs:', error);
      setError('Failed to load available SKUs');
    } finally {
      setLoadingSkus(false);
    }
  };

  // 🆕 NEW: Toggle auto import
  const handleToggleAutoImport = async () => {
    if (!isUser()) {
      setError('You do not have permission to change import settings');
      return;
    }

    try {
      setImportControlsLoading(true);
      const newValue = !autoImportEnabled;
      
      await axios.post('/api/settings/import/toggle-auto-import', { enabled: newValue });
      
      setAutoImportEnabled(newValue);
      setSuccess(`Auto-import ${newValue ? 'enabled' : 'disabled'} successfully`);
    } catch (error) {
      console.error('Failed to toggle auto import:', error);
      setError('Failed to update auto-import setting');
    } finally {
      setImportControlsLoading(false);
    }
  };

  // 🆕 NEW: Toggle auto transfer
  const handleToggleAutoTransfer = async () => {
    if (!isUser()) {
      setError('You do not have permission to change auto transfer settings');
      return;
    }

    try {
      setImportControlsLoading(true);
      const response = await axios.post('/api/settings/import/toggle-auto-transfer', {
        enabled: !autoTransferEnabled
      });
      
      setAutoTransferEnabled(response.data.autoTransferEnabled);
      setSuccess(response.data.message || `Auto transfer ${response.data.autoTransferEnabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error('Failed to toggle auto transfer:', error);
      setError('Failed to update auto transfer setting');
    } finally {
      setImportControlsLoading(false);
    }
  };

  // 🆕 NEW: Toggle auto tracking sync
  const handleToggleAutoTrackingSync = async () => {
    if (!isUser()) {
      setError('You do not have permission to change auto tracking sync settings');
      return;
    }

    try {
      setImportControlsLoading(true);
      const newValue = !autoTrackingSyncEnabled;
      
      await axios.post('/api/settings/import/toggle-auto-tracking-sync', { enabled: newValue });
      
      setAutoTrackingSyncEnabled(newValue);
      setSuccess(`Auto tracking sync ${newValue ? 'enabled' : 'disabled'} successfully`);
    } catch (error) {
      console.error('Failed to toggle auto tracking sync:', error);
      setError('Failed to update auto tracking sync setting');
    } finally {
      setImportControlsLoading(false);
    }
  };

  // 🔧 ENHANCED: Manual transfer with automatic status fix before transfer
  const handleManualTransfer = async () => {
    if (!isUser()) {
      setError('You do not have permission to transfer orders');
      return;
    }

    if (selectedRows.length === 0) {
      setError('Please select orders to transfer');
      return;
    }

    // ✅ Check for already-sent orders before confirming
    const selectedOrders = orders.filter(order => selectedRows.includes(order.id));
    const alreadySentOrders = selectedOrders.filter(order => order.sent_to_fulfillment_at);
    const validOrders = selectedOrders.filter(order => !order.sent_to_fulfillment_at);

    let confirmMessage = `Are you sure you want to send ${selectedRows.length} order(s) to their assigned fulfillment centers?`;
    
    if (alreadySentOrders.length > 0) {
      confirmMessage = `Warning: ${alreadySentOrders.length} of the selected orders have already been sent to fulfillment centers and will be skipped.\n\n`;
      confirmMessage += `Orders to transfer: ${validOrders.length}\n`;
      confirmMessage += `Already sent: ${alreadySentOrders.length}\n\n`;
      confirmMessage += `Continue with transfer?`;
      
      if (validOrders.length === 0) {
        setError('All selected orders have already been sent to fulfillment centers');
        return;
      }
    }

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setImportControlsLoading(true);
      
      // 🔧 FIRST: Fix any status inconsistencies before transfer
      console.log('Running status fix before transfer...');
      const statusFixResult = await fixStatusInconsistencies(true);
      if (statusFixResult.fixedCount > 0) {
        console.log(`Fixed ${statusFixResult.fixedCount} status inconsistencies before transfer`);
      }
      
      const response = await axios.post('/api/orders/bulk/transfer', {
        orderIds: selectedRows
      });
      
      if (response.data.success) {
        const { transferred, failed, status_corrected } = response.data.results;
        let message = `Successfully transferred ${transferred} order(s) to fulfillment centers`;
        
        if (status_corrected > 0) {
          message += `. ${status_corrected} order status(es) corrected from Pending to Sent`;
        }
        
        // Include status fix count if applicable
        if (statusFixResult.fixedCount > 0) {
          message += `. Pre-transfer: fixed ${statusFixResult.fixedCount} status inconsistencies`;
        }
        
        if (failed > 0) {
          setError(`${message}. ${failed} failed. Check individual order details for errors.`);
        } else {
          setSuccess(message);
        }
        setSelectedRows([]);
        setSelectAllFiltered(false);
        fetchOrders(paginationModel);
      }
    } catch (error) {
      console.error('Failed to transfer orders:', error);
      
      // ✅ Handle specific error messages for already-sent orders
      if (error.response?.data?.already_sent_orders) {
        const alreadySent = error.response.data.already_sent_orders;
        const errorMsg = `${alreadySent.length} orders were already sent:\n` + 
          alreadySent.map(o => `• Order ${o.order_number} (sent ${new Date(o.sent_at).toLocaleDateString()})`).join('\n');
        setError(errorMsg);
      } else {
        setError(error.response?.data?.error || 'Failed to transfer orders to fulfillment centers');
      }
    } finally {
      setImportControlsLoading(false);
    }
  };

  return (
    <Box className="page-container" sx={{ flexGrow: 1, p: { xs: 2, sm: 3 } }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' }, 
        mb: 3,
        gap: { xs: 2, sm: 0 }
      }}>
        <Typography 
          variant="h4" 
          component="h1" 
          sx={{ 
            fontWeight: 700,
            background: 'linear-gradient(145deg, #ffffff, #e0e7ff)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontSize: { xs: '1.75rem', sm: '2rem', md: '2.25rem' }
          }}
        >
          Order Management
        </Typography>
        <Box sx={{ 
          display: 'flex', 
          gap: 1.5,
          flexDirection: { xs: 'column', sm: 'row' },
          width: { xs: '100%', sm: 'auto' }
        }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={handleRefreshOrders}
            disabled={loading}
            sx={{ minWidth: { xs: '100%', sm: 120 } }}
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </Button>
          <Tooltip 
            title={`Export ${
              selectedRows.length > 0 
                ? `${selectedRows.length} selected` 
                : Object.values(filters).some(value => value && value.trim && value.trim() !== '') 
                  ? 'all filtered' 
                  : 'all'
            } orders to CSV`} 
            arrow
          >
            <span>
              <Button
                variant="contained"
                startIcon={<GetApp />}
                onClick={handleExportCSV}
                disabled={!orders || orders.length === 0}
                sx={{ 
                  minWidth: { xs: '100%', sm: 140 },
                  background: 'linear-gradient(145deg, rgba(34, 197, 94, 0.9), rgba(22, 163, 74, 0.9))',
                  '&:hover': {
                    background: 'linear-gradient(145deg, rgba(34, 197, 94, 1), rgba(22, 163, 74, 1))',
                  },
                  '&:disabled': {
                    background: 'rgba(156, 163, 175, 0.5)',
                    color: 'rgba(255, 255, 255, 0.5)'
                  }
                }}
              >
                Export CSV
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* 🆕 NEW: Import Control Panel */}
      <Paper className="glass-card" sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 600, display: 'flex', alignItems: 'center' }}>
          <LocalShipping sx={{ mr: 1, verticalAlign: 'middle' }} />
          Import & Transfer Controls
        </Typography>
        
        <Grid container spacing={3} alignItems="center">
          {/* Auto Import Toggle */}
          <Grid item xs={12} sm={6} md={3}>
            <FormControlLabel
              control={
                <Switch
                  checked={autoImportEnabled}
                  onChange={handleToggleAutoImport}
                  disabled={!isUser() || importControlsLoading}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#4ade80',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#22c55e',
                    },
                  }}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#ffffff' }}>
                    Auto Import
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                    {autoImportEnabled ? 'Orders import automatically' : 'Manual import only'}
                  </Typography>
                </Box>
              }
            />
          </Grid>

          {/* Auto Transfer Toggle */}
          <Grid item xs={12} sm={6} md={3}>
            <FormControlLabel
              control={
                <Switch
                  checked={autoTransferEnabled}
                  onChange={handleToggleAutoTransfer}
                  disabled={!isUser() || importControlsLoading}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#60a5fa',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#3b82f6',
                    },
                  }}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#ffffff' }}>
                    Auto Transfer
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                    {autoTransferEnabled ? 'Send to fulfillment centers automatically' : 'Manual review before sending'}
                  </Typography>
                </Box>
              }
            />
          </Grid>

          {/* Auto Tracking Sync Toggle */}
          <Grid item xs={12} sm={6} md={3}>
            <FormControlLabel
              control={
                <Switch
                  checked={autoTrackingSyncEnabled}
                  onChange={handleToggleAutoTrackingSync}
                  disabled={!isUser() || importControlsLoading}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#f59e0b',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#d97706',
                    },
                  }}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#ffffff' }}>
                    Auto Tracking Sync
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                    {autoTrackingSyncEnabled ? 'Sync tracking numbers automatically' : 'Manual tracking sync only'}
                  </Typography>
                </Box>
              }
            />
          </Grid>

          {/* Status Info */}
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ textAlign: { xs: 'left', md: 'right' } }}>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 600 }}>
                Current Mode:
              </Typography>
              <Typography variant="caption" sx={{ color: autoImportEnabled && autoTransferEnabled ? '#4ade80' : autoImportEnabled ? '#fbbf24' : '#fca5a5' }}>
                {autoImportEnabled && autoTransferEnabled ? '🚀 Fully Automated' : 
                 autoImportEnabled ? '⚡ Auto Import Only' : 
                 '⏸️ Manual Control'}
              </Typography>
              {autoTrackingSyncEnabled && (
                <Typography variant="caption" sx={{ display: 'block', color: '#f59e0b', mt: 0.5 }}>
                  📍 Auto Tracking Sync
                </Typography>
              )}
            </Box>
          </Grid>

          {/* Manual Action Buttons Row */}
          <Grid item xs={12}>
            <Box sx={{ 
              display: 'flex', 
              gap: 2, 
              mt: 2,
              flexWrap: 'wrap',
              justifyContent: { xs: 'center', sm: 'flex-start' }
            }}>
              <Button
                variant="contained"
                onClick={() => setSyncDialogOpen(true)}
                disabled={!isUser() || syncLoading}
                startIcon={<Download />}
                sx={{
                  background: 'linear-gradient(145deg, rgba(34, 197, 94, 0.9), rgba(22, 163, 74, 0.9))',
                  '&:hover': {
                    background: 'linear-gradient(145deg, rgba(34, 197, 94, 1), rgba(22, 163, 74, 1))',
                  }
                }}
              >
                Manual Import
              </Button>
              
              <Button
                variant="contained"
                onClick={handleManualTrackingSync}
                disabled={!isUser() || trackingSyncLoading}
                startIcon={trackingSyncLoading ? <CircularProgress size={20} sx={{ color: 'inherit' }} /> : <LocalShipping />}
                sx={{
                  background: 'linear-gradient(145deg, rgba(245, 158, 11, 0.9), rgba(217, 119, 6, 0.9))',
                  '&:hover': {
                    background: 'linear-gradient(145deg, rgba(245, 158, 11, 1), rgba(217, 119, 6, 1))',
                  }
                }}
              >
                {trackingSyncLoading ? 'Syncing...' : 'Sync Tracking'}
              </Button>
              
              <Tooltip title="Search MetaKocka for all orders (regardless of current assignment) and update status" arrow>
                <Button
                  variant="contained"
                  onClick={handleMetaKockaReconcile}
                  disabled={!isUser() || reconcileLoading}
                  startIcon={reconcileLoading ? <CircularProgress size={20} sx={{ color: 'inherit' }} /> : <RestartAlt />}
                  sx={{
                    background: 'linear-gradient(145deg, rgba(139, 69, 19, 0.9), rgba(101, 67, 33, 0.9))',
                    '&:hover': {
                      background: 'linear-gradient(145deg, rgba(139, 69, 19, 1), rgba(101, 67, 33, 1))',
                    }
                  }}
                >
                  {reconcileLoading ? 'Reconciling...' : 'Find in MetaKocka'}
                </Button>
              </Tooltip>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Filters */}
      <Paper className="glass-card" sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
          <FilterList sx={{ mr: 1, verticalAlign: 'middle' }} />
          Filters & Search
        </Typography>
        <Grid container spacing={2} alignItems="center">
          {/* Search Field - Full width on mobile, half on tablet, 1/3 on desktop */}
          <Grid item xs={12} sm={6} lg={4}>
            <TextField
              fullWidth
              label="Search Orders"
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              placeholder="Order number, email..."
              size="small"
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'rgba(255,255,255,0.7)' }} />
              }}
              helperText="Search filters automatically as you type"
            />
          </Grid>
          
          {/* Status Select */}
          <Grid item xs={6} sm={3} lg={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                label="Status"
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="sent">Sent</MenuItem>
                <MenuItem value="fulfilled">Fulfilled</MenuItem>
                <MenuItem value="Problem">Problem</MenuItem>
                <MenuItem value="delivered">Delivered</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          {/* Fulfillment Center Select */}
          <Grid item xs={6} sm={3} lg={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Center</InputLabel>
              <Select
                value={filters.fulfillmentCenter}
                onChange={(e) => handleFilterChange('fulfillmentCenter', e.target.value)}
                label="Center"
              >
                <MenuItem value="">All Centers</MenuItem>
                <MenuItem value="metakocka">MetaKocka</MenuItem>
                <MenuItem value="eliz">Eli Z</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          {/* Payment Method Select */}
          <Grid item xs={6} sm={3} lg={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Payment Method</InputLabel>
              <Select
                value={filters.paymentMethod}
                onChange={(e) => handleFilterChange('paymentMethod', e.target.value)}
                label="Payment Method"
              >
                <MenuItem value="">All Methods</MenuItem>
                <MenuItem value="Unknown">Unknown</MenuItem>
                <MenuItem value="Cash on Delivery (COD)">Cash on Delivery (COD)</MenuItem>
                <MenuItem value="shopify_payments">Shopify Payments</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          {/* Date From */}
          <Grid item xs={6} sm={6} lg={2}>
            <TextField
              fullWidth
              type="date"
              label="From Date"
              value={filters.dateFrom}
              onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>
          
          {/* Date To */}
          <Grid item xs={6} sm={6} lg={2}>
            <TextField
              fullWidth
              type="date"
              label="To Date"
              value={filters.dateTo}
              onChange={(e) => handleFilterChange('dateTo', e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>
          
          {/* SKU Filter */}
          <Grid item xs={12} sm={6} lg={3}>
            <Autocomplete
              size="small"
              options={availableSkus}
              value={filters.sku}
              onChange={(event, newValue) => handleFilterChange('sku', newValue || '')}
              loading={loadingSkus}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Product SKU"
                  placeholder="Select SKU to filter..."
                  helperText="Filter orders by product SKU"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingSkus ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              noOptionsText="No SKUs available"
              freeSolo
              clearOnEscape
            />
          </Grid>
          
          {/* SKU Filter Type */}
          <Grid item xs={12} sm={6} lg={3}>
            <FormControl fullWidth size="small">
              <InputLabel>SKU Filter Type</InputLabel>
              <Select
                value={filters.skuFilterType}
                onChange={(e) => handleFilterChange('skuFilterType', e.target.value)}
                label="SKU Filter Type"
                disabled={!filters.sku.trim()}
              >
                <MenuItem value="contains_also">Contains Also</MenuItem>
                <MenuItem value="contains_only">Contains Only</MenuItem>
                <MenuItem value="doesnt_include">Doesn't Include</MenuItem>
                <MenuItem value="doesnt_include_only">Doesn't Include Only</MenuItem>
              </Select>
            </FormControl>
            {filters.sku.trim() && (
              <Box sx={{ mt: 0.5 }}>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.7rem' }}>
                  {filters.skuFilterType === 'contains_only' 
                    ? 'Orders with ONLY this SKU' 
                    : filters.skuFilterType === 'contains_also'
                    ? 'Orders containing this SKU'
                    : filters.skuFilterType === 'doesnt_include'
                    ? 'Orders NOT containing this SKU'
                    : filters.skuFilterType === 'doesnt_include_only'
                    ? 'Orders with at least one item that is NOT this SKU'
                    : 'Orders containing this SKU'
                  }
                </Typography>
              </Box>
            )}
          </Grid>
        </Grid>
        
        {/* Action Buttons - Only Clear Filters since filtering is now automatic */}
        <Box sx={{ 
          display: 'flex', 
          gap: 2, 
          mt: 3, 
          flexWrap: 'wrap',
          justifyContent: { xs: 'center', sm: 'flex-start' }
        }}>
          <Button
            variant="outlined"
            onClick={handleClearFilters}
            startIcon={<FilterList />}
            sx={{ 
              borderColor: 'rgba(255,255,255,0.3)',
              color: 'rgba(255,255,255,0.9)',
              '&:hover': {
                borderColor: 'rgba(255,255,255,0.5)',
                backgroundColor: 'rgba(255,255,255,0.05)',
              }
            }}
          >
            Clear Filters
          </Button>
        </Box>
      </Paper>

      {/* Bulk Actions Toolbar */}
      {selectedRows.length > 0 && (
        <Paper 
          className="glass-card" 
          sx={{ 
            mb: 2,
            p: 2,
            backgroundColor: 'rgba(99, 102, 241, 0.1)',
            border: '1px solid rgba(99, 102, 241, 0.3)'
          }}
        >
          <Toolbar sx={{ px: 0, minHeight: '48px !important' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flex: '1 1 100%' }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: '#ffffff',
                  fontWeight: 600,
                  mr: 2
                }}
              >
                {selectedRows.length} order{selectedRows.length > 1 ? 's' : ''} selected
              </Typography>
              
              {/* 🆕 NEW: Select All Toggle */}
              {!selectAllFiltered && (
                <Tooltip title="Select all filtered orders across all pages">
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={isSelectingAllFiltered ? <CircularProgress size={16} /> : <SelectAll />}
                    onClick={handleSelectAllFiltered}
                    disabled={isSelectingAllFiltered}
                    sx={{
                      borderColor: 'rgba(34, 197, 94, 0.3)',
                      color: '#4ade80',
                      fontSize: '0.75rem',
                      py: 0.5,
                      mr: 2,
                      '&:hover': {
                        borderColor: 'rgba(34, 197, 94, 0.5)',
                        backgroundColor: 'rgba(34, 197, 94, 0.1)'
                      }
                    }}
                  >
                    {isSelectingAllFiltered ? 'Selecting...' : 'Select All Filtered'}
                  </Button>
                </Tooltip>
              )}
              
              {/* 🆕 NEW: Clear Selection Button */}
              {(selectAllFiltered || selectedRows.length > 0) && (
                <Tooltip title="Clear selection">
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Cancel />}
                    onClick={handleClearSelection}
                    sx={{
                      borderColor: 'rgba(239, 68, 68, 0.3)',
                      color: '#fca5a5',
                      fontSize: '0.75rem',
                      py: 0.5,
                      mr: 2,
                      '&:hover': {
                        borderColor: 'rgba(239, 68, 68, 0.5)',
                        backgroundColor: 'rgba(239, 68, 68, 0.1)'
                      }
                    }}
                  >
                    Clear
                  </Button>
                </Tooltip>
              )}
              
              {/* 🆕 NEW: Selection Status Indicator */}
              {selectAllFiltered && (
                <Chip
                  label="All Filtered Selected"
                  size="small"
                  sx={{
                    backgroundColor: 'rgba(34, 197, 94, 0.2)',
                    color: '#4ade80',
                    border: '1px solid rgba(34, 197, 94, 0.3)',
                    fontWeight: 600,
                    fontSize: '0.75rem'
                  }}
                />
              )}
            </Box>
            
            {isUser() && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip title="Send to Fulfillment Centers">
                  <Button
                    variant="contained"
                    startIcon={<LocalShipping />}
                    onClick={handleManualTransfer}
                    disabled={importControlsLoading}
                    sx={{ 
                      backgroundColor: 'rgba(99, 102, 241, 0.2)',
                      color: '#a5b4fc',
                      border: '1px solid rgba(99, 102, 241, 0.3)',
                      '&:hover': {
                        backgroundColor: 'rgba(99, 102, 241, 0.3)',
                      }
                    }}
                  >
                    Send to Fulfillment
                  </Button>
                </Tooltip>
                
                <Tooltip title="Update Status">
                  <Button
                    variant="contained"
                    startIcon={<Edit />}
                    onClick={() => setBulkStatusDialogOpen(true)}
                    sx={{ 
                      backgroundColor: 'rgba(34, 197, 94, 0.2)',
                      color: '#4ade80',
                      border: '1px solid rgba(34, 197, 94, 0.3)',
                      '&:hover': {
                        backgroundColor: 'rgba(34, 197, 94, 0.3)',
                      }
                    }}
                  >
                    Status
                  </Button>
                </Tooltip>
                
                <Tooltip title="Change Fulfillment Center">
                  <Button
                    variant="contained"
                    startIcon={<Edit />}
                    onClick={() => setBulkFulfillmentDialogOpen(true)}
                    sx={{ 
                      backgroundColor: 'rgba(168, 85, 247, 0.2)',
                      color: '#a78bfa',
                      border: '1px solid rgba(168, 85, 247, 0.3)',
                      '&:hover': {
                        backgroundColor: 'rgba(168, 85, 247, 0.3)',
                      }
                    }}
                  >
                    Change FC
                  </Button>
                </Tooltip>
                
                <Tooltip title="Cancel Orders">
                  <Button
                    variant="contained"
                    startIcon={<Block />}
                    onClick={handleBulkCancel}
                    sx={{ 
                      backgroundColor: 'rgba(251, 146, 60, 0.2)',
                      color: '#fb923c',
                      border: '1px solid rgba(251, 146, 60, 0.3)',
                      '&:hover': {
                        backgroundColor: 'rgba(251, 146, 60, 0.3)',
                      }
                    }}
                  >
                    Cancel
                  </Button>
                </Tooltip>
                
                <Tooltip title="More Actions">
                  <IconButton
                    onClick={handleBulkMenuOpen}
                    sx={{ 
                      color: 'rgba(255, 255, 255, 0.9)',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      }
                    }}
                  >
                    <MoreVert />
                  </IconButton>
                </Tooltip>
                
                <Menu
                  anchorEl={bulkMenuAnchor}
                  open={Boolean(bulkMenuAnchor)}
                  onClose={handleBulkMenuClose}
                  PaperProps={{
                    className: 'glass-card',
                    sx: { mt: 1 }
                  }}
                >
                  <MenuItem onClick={handleBulkDelete} sx={{ color: '#ef4444' }}>
                    <ListItemIcon>
                      <Delete sx={{ color: '#ef4444' }} />
                    </ListItemIcon>
                    <ListItemText primary="Delete Orders" />
                  </MenuItem>
                </Menu>
              </Box>
            )}
          </Toolbar>
        </Paper>
      )}

      {/* Orders Table */}
      <Paper 
        className="glass-card" 
        sx={{ 
          height: { xs: 500, sm: 600, lg: 700 }, 
          width: '100%',
          overflow: 'hidden'
        }}
      >
        <DataGrid
          rows={orders}
          columns={columns}
          loading={loading}
          checkboxSelection
          disableRowSelectionOnClick
          onRowSelectionModelChange={setSelectedRows}
          rowSelectionModel={selectedRows}
          // 🆕 NEW: Server-side pagination
          pagination
          paginationMode="server"
          rowCount={totalOrders}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          pageSizeOptions={[10, 25, 50, 100]}
          sx={{
            border: 'none',
            '& .MuiDataGrid-main': {
              border: 'none'
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: 'rgba(255, 255, 255, 0.08)',
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            },
            '& .MuiDataGrid-columnHeader': {
              color: '#ffffff',
              fontWeight: 600,
            },
            '& .MuiDataGrid-cell': {
              color: 'rgba(255, 255, 255, 0.95)',
              borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
            },
            '& .MuiDataGrid-footerContainer': {
              borderTop: '1px solid rgba(255, 255, 255, 0.1)',
              backgroundColor: 'rgba(255, 255, 255, 0.03)',
            },
            '& .MuiTablePagination-root': {
              color: 'rgba(255, 255, 255, 0.9)',
            },
            '& .MuiCheckbox-root': {
              color: 'rgba(255, 255, 255, 0.7)',
              '&.Mui-checked': {
                color: 'rgba(99, 102, 241, 0.9)',
              }
            }
          }}
        />
      </Paper>

      {/* Order Details Dialog */}
      <Dialog open={dialogOpen} onClose={() => {
        setDialogOpen(false);
        setTrackingInfo(null);
        setTrackingLoading(false);
      }} maxWidth="md" fullWidth>
        <DialogTitle>
          Order Details - #{selectedOrder?.shopify_order_number}
        </DialogTitle>
        <DialogContent>
          {selectedOrder && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card className="glass-card" sx={{ height: 'fit-content' }}>
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="h6" gutterBottom sx={{ color: '#ffffff', fontWeight: 600, mb: 3 }}>
                        Order Information
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', fontWeight: 500 }}>
                            Status:
                          </Typography>
                          {editMode ? (
                            <FormControl size="small" sx={{ minWidth: 120 }}>
                              <Select
                                value={editedOrder?.status || selectedOrder.status}
                                onChange={(e) => setEditedOrder(prev => ({ ...prev, status: e.target.value }))}
                                size="small"
                                sx={{
                                  backgroundColor: 'rgba(255, 255, 255, 0.08)',
                                  color: '#ffffff',
                                  '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'rgba(255, 255, 255, 0.2)',
                                  },
                                  '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'rgba(255, 255, 255, 0.3)',
                                  },
                                  '& .MuiSvgIcon-root': {
                                    color: '#ffffff',
                                  },
                                }}
                              >
                                <MenuItem value="pending">Pending</MenuItem>
                                <MenuItem value="Problem">Problem</MenuItem>
                                <MenuItem value="processing">Processing</MenuItem>
                                <MenuItem value="Sent">Sent</MenuItem>
                                <MenuItem value="Shipped">Shipped</MenuItem>
                                <MenuItem value="Delivered">Delivered</MenuItem>
                                <MenuItem value="Cancelled">Cancelled</MenuItem>
                              </Select>
                            </FormControl>
                          ) : (
                            <Chip
                              label={selectedOrder.status}
                              className={`status-${selectedOrder.status}`}
                              size="small"
                              sx={{ fontWeight: 600, textTransform: 'capitalize' }}
                            />
                          )}
                        </Box>
                        
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', fontWeight: 500 }}>
                            Total Amount:
                          </Typography>
                          <Typography variant="h6" sx={{ color: '#22c55e', fontWeight: 700 }}>
                            €{parseFloat(selectedOrder.total_amount || 0).toFixed(2)}
                          </Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', fontWeight: 500 }}>
                            Customer:
                          </Typography>
                          {editMode ? (
                            <TextField
                              value={editedOrder?.customer_email || selectedOrder.customer_email}
                              onChange={(e) => setEditedOrder(prev => ({ ...prev, customer_email: e.target.value }))}
                              size="small"
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  backgroundColor: 'rgba(255, 255, 255, 0.08)',
                                  color: '#ffffff',
                                  '& fieldset': {
                                    borderColor: 'rgba(255, 255, 255, 0.2)',
                                  },
                                  '&:hover fieldset': {
                                    borderColor: 'rgba(255, 255, 255, 0.3)',
                                  },
                                },
                                '& .MuiInputLabel-root': {
                                  color: 'rgba(255, 255, 255, 0.7)',
                                },
                                minWidth: 200
                              }}
                            />
                          ) : (
                            <Typography variant="body2" sx={{ color: '#ffffff', fontWeight: 600 }}>
                              {selectedOrder.customer_email}
                            </Typography>
                          )}
                        </Box>
                        
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', fontWeight: 500 }}>
                            Payment Method:
                          </Typography>
                          {editMode ? (
                            <FormControl size="small" sx={{ minWidth: 180 }}>
                              <Select
                                value={editedOrder?.payment_method || selectedOrder.payment_method || 'Unknown'}
                                onChange={(e) => {
                                  const newPaymentMethod = e.target.value;
                                  console.log('Payment method changed to:', newPaymentMethod);
                                  setEditedOrder(prev => ({ ...prev, payment_method: newPaymentMethod }));
                                  // Re-run assignment logic when payment method changes
                                  handleReassignFulfillmentCenter(newPaymentMethod);
                                }}
                                size="small"
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.08)',
                                    color: '#ffffff',
                                    '& fieldset': {
                                      borderColor: 'rgba(255, 255, 255, 0.2)',
                                    },
                                    '&:hover fieldset': {
                                      borderColor: 'rgba(255, 255, 255, 0.3)',
                                    },
                                  },
                                  '& .MuiSelect-icon': {
                                    color: 'rgba(255, 255, 255, 0.7)',
                                  },
                                }}
                              >
                                <MenuItem value="Unknown">Unknown</MenuItem>
                                <MenuItem value="shopify_payments">Shopify Payments</MenuItem>
                                <MenuItem value="Cash on Delivery (COD)">Cash on Delivery (COD)</MenuItem>
                                <MenuItem value="Contrassegno (+5,00€)">Contrassegno (+5,00€)</MenuItem>
                                <MenuItem value="paypal">PayPal</MenuItem>
                                <MenuItem value="stripe">Stripe</MenuItem>
                                <MenuItem value="manual">Manual Payment</MenuItem>
                              </Select>
                            </FormControl>
                          ) : (
                            <Chip
                              label={selectedOrder.payment_method || 'Unknown'}
                              variant="outlined"
                              size="small"
                              sx={{ 
                                backgroundColor: (() => {
                                  const method = selectedOrder.payment_method;
                                  if (!method || method === 'Unknown') return 'rgba(156, 163, 175, 0.15)';
                                  if (method.includes('Contrassegno') || method.toLowerCase().includes('cod')) return 'rgba(245, 158, 11, 0.15)';
                                  if (method.toLowerCase().includes('shopify_payments') || method.toLowerCase().includes('stripe')) return 'rgba(34, 197, 94, 0.15)';
                                  if (method.toLowerCase().includes('paypal')) return 'rgba(59, 130, 246, 0.15)';
                                  return 'rgba(168, 85, 247, 0.15)';
                                })(),
                                color: (() => {
                                  const method = selectedOrder.payment_method;
                                  if (!method || method === 'Unknown') return '#9ca3af';
                                  if (method.includes('Contrassegno') || method.toLowerCase().includes('cod')) return '#fbbf24';
                                  if (method.toLowerCase().includes('shopify_payments') || method.toLowerCase().includes('stripe')) return '#4ade80';
                                  if (method.toLowerCase().includes('paypal')) return '#60a5fa';
                                  return '#c4b5fd';
                                })(),
                                borderColor: (() => {
                                  const method = selectedOrder.payment_method;
                                  if (!method || method === 'Unknown') return 'rgba(156, 163, 175, 0.3)';
                                  if (method.includes('Contrassegno') || method.toLowerCase().includes('cod')) return 'rgba(245, 158, 11, 0.3)';
                                  if (method.toLowerCase().includes('shopify_payments') || method.toLowerCase().includes('stripe')) return 'rgba(34, 197, 94, 0.3)';
                                  if (method.toLowerCase().includes('paypal')) return 'rgba(59, 130, 246, 0.3)';
                                  return 'rgba(168, 85, 247, 0.3)';
                                })(),
                                fontWeight: 600
                              }}
                            />
                          )}
                        </Box>
                        
                        {selectedOrder.shipping_method && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', fontWeight: 500 }}>
                              Shipping Method:
                            </Typography>
                            <Chip
                              label={selectedOrder.shipping_method}
                              variant="outlined"
                              size="small"
                              sx={{ 
                                backgroundColor: 'rgba(34, 197, 94, 0.2)',
                                color: '#4ade80',
                                border: '1px solid rgba(34, 197, 94, 0.3)',
                                fontWeight: 600
                              }}
                            />
                          </Box>
                        )}
                        
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', fontWeight: 500 }}>
                            Fulfillment Center:
                          </Typography>
                          {isUser() && selectedOrder.status !== 'sent' ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <FormControl size="small" sx={{ minWidth: 180 }}>
                                <Select
                                  value={selectedOrder.fulfillment_center_id || ''}
                                  onChange={(e) => handleChangeFulfillmentCenter(selectedOrder.id, e.target.value)}
                                  disabled={changingFulfillmentCenter}
                                  size="small"
                                  sx={{
                                    backgroundColor: 'rgba(255, 255, 255, 0.08)',
                                    color: '#ffffff',
                                    '& .MuiOutlinedInput-notchedOutline': {
                                      borderColor: 'rgba(255, 255, 255, 0.2)',
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                      borderColor: 'rgba(255, 255, 255, 0.3)',
                                    },
                                    '& .MuiSvgIcon-root': {
                                      color: '#ffffff',
                                    },
                                  }}
                                >
                                  <MenuItem value="">
                                    <em>Unassigned</em>
                                  </MenuItem>
                                  {fulfillmentCenters.map((center) => (
                                    <MenuItem key={center.id} value={center.id}>
                                      {center.name}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                              {changingFulfillmentCenter && (
                                <CircularProgress size={20} sx={{ color: '#ffffff' }} />
                              )}
                            </Box>
                          ) : (
                            <Chip
                              label={selectedOrder.fulfillment_centers?.name || 'Unassigned'}
                              variant="outlined"
                              size="small"
                              sx={{ 
                                backgroundColor: (() => {
                                  const centerName = selectedOrder.fulfillment_centers?.name;
                                  if (!centerName || centerName === 'Unassigned') return 'rgba(239, 68, 68, 0.15)';
                                  if (centerName.toLowerCase() === 'metakocka') return 'rgba(34, 197, 94, 0.15)';
                                  if (centerName.toLowerCase().includes('eliz')) return 'rgba(99, 102, 241, 0.15)';
                                  return 'rgba(168, 85, 247, 0.15)';
                                })(),
                                color: (() => {
                                  const centerName = selectedOrder.fulfillment_centers?.name;
                                  if (!centerName || centerName === 'Unassigned') return '#fca5a5';
                                  if (centerName.toLowerCase() === 'metakocka') return '#4ade80';
                                  if (centerName.toLowerCase().includes('eliz')) return '#a5b4fc';
                                  return '#c4b5fd';
                                })(),
                                borderColor: (() => {
                                  const centerName = selectedOrder.fulfillment_centers?.name;
                                  if (!centerName || centerName === 'Unassigned') return 'rgba(239, 68, 68, 0.3)';
                                  if (centerName.toLowerCase() === 'metakocka') return 'rgba(34, 197, 94, 0.3)';
                                  if (centerName.toLowerCase().includes('eliz')) return 'rgba(99, 102, 241, 0.3)';
                                  return 'rgba(168, 85, 247, 0.3)';
                                })(),
                                fontWeight: 600
                              }}
                            />
                          )}
                        </Box>
                        
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', fontWeight: 500 }}>
                            Created:
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', fontFamily: 'monospace' }}>
                            {new Date(selectedOrder.created_at).toLocaleString()}
                          </Typography>
                        </Box>
                        
                        {/* 🚚 NEW: Yuntrack Tracking Button */}
                        {selectedOrder.external_tracking_number && selectedOrder.fulfillment_centers?.code === 'eliz' && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', fontWeight: 500 }}>
                              Yuntrack Tracking:
                            </Typography>
                            <Button
                              variant="contained"
                              size="small"
                              sx={{
                                backgroundColor: 'rgba(245, 158, 11, 0.2)',
                                color: '#fbbf24',
                                border: '1px solid rgba(245, 158, 11, 0.4)',
                                fontWeight: 600,
                                fontFamily: 'monospace',
                                '&:hover': {
                                  backgroundColor: 'rgba(245, 158, 11, 0.3)',
                                }
                              }}
                              onClick={() => {
                                // Open Yuntrack tracking page
                                const yuntrackUrl = `https://www.yuntrack.com/parcelTracking?id=${selectedOrder.external_tracking_number}`;
                                window.open(yuntrackUrl, '_blank');
                              }}
                              title="Track on Yuntrack website"
                            >
                              {selectedOrder.external_tracking_number} 🔗
                            </Button>
                          </Box>
                        )}
                        
                        {/* 🇮🇹 NEW: Poste Italiane Last Mile Tracking */}
                        {selectedOrder.last_mile_tracking && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', fontWeight: 500 }}>
                              Poste Italiane:
                            </Typography>
                            <Button
                              variant="contained"
                              size="small"
                              sx={{
                                backgroundColor: 'rgba(34, 197, 94, 0.2)',
                                color: '#4ade80',
                                border: '1px solid rgba(34, 197, 94, 0.4)',
                                fontWeight: 600,
                                fontFamily: 'monospace',
                                '&:hover': {
                                  backgroundColor: 'rgba(34, 197, 94, 0.3)',
                                }
                              }}
                              onClick={() => {
                                // Open Poste Italiane tracking page
                                const posteUrl = `https://www.poste.it/cerca/index.shtml#/risultati-spedizioni/${selectedOrder.last_mile_tracking}`;
                                window.open(posteUrl, '_blank');
                              }}
                              title="Track on Poste Italiane website"
                            >
                              {selectedOrder.last_mile_tracking} 🇮🇹
                            </Button>
                          </Box>
                        )}
                        
                        {/* For non-EliZ orders, show regular tracking */}
                        {selectedOrder.external_tracking_number && selectedOrder.fulfillment_centers?.code !== 'eliz' && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', fontWeight: 500 }}>
                              Tracking Number:
                            </Typography>
                            <Chip
                              label={selectedOrder.external_tracking_number}
                              variant="outlined"
                              size="small"
                              sx={{ 
                                backgroundColor: 'rgba(245, 158, 11, 0.15)',
                                color: '#fbbf24',
                                borderColor: 'rgba(245, 158, 11, 0.3)',
                                fontWeight: 600,
                                fontFamily: 'monospace',
                                '&:hover': {
                                  backgroundColor: 'rgba(245, 158, 11, 0.25)',
                                  cursor: 'pointer'
                                }
                              }}
                              onClick={() => {
                                // Copy tracking number to clipboard
                                navigator.clipboard.writeText(selectedOrder.external_tracking_number);
                                setSuccess('Tracking number copied to clipboard');
                              }}
                              title="Click to copy tracking number"
                            />
                          </Box>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card className="glass-card">
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ color: '#ffffff', fontWeight: 600, mb: 3 }}>
                        Shipping Address
                        {editMode && (
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', ml: 2 }}>
                            (Editable)
                          </Typography>
                        )}
                      </Typography>
                      {editMode ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <TextField
                            label="Recipient Name"
                            value={editedOrder?.shipping_address?.name || ''}
                            onChange={(e) => handleUpdateShippingAddress('name', e.target.value)}
                            size="small"
                            fullWidth
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                                color: '#ffffff',
                                '& fieldset': {
                                  borderColor: 'rgba(255, 255, 255, 0.2)',
                                },
                                '&:hover fieldset': {
                                  borderColor: 'rgba(255, 255, 255, 0.3)',
                                },
                              },
                              '& .MuiInputLabel-root': {
                                color: 'rgba(255, 255, 255, 0.7)',
                              },
                            }}
                          />
                          <TextField
                            label="Address Line 1"
                            value={editedOrder?.shipping_address?.address1 || ''}
                            onChange={(e) => handleUpdateShippingAddress('address1', e.target.value)}
                            size="small"
                            fullWidth
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                                color: '#ffffff',
                                '& fieldset': {
                                  borderColor: 'rgba(255, 255, 255, 0.2)',
                                },
                                '&:hover fieldset': {
                                  borderColor: 'rgba(255, 255, 255, 0.3)',
                                },
                              },
                              '& .MuiInputLabel-root': {
                                color: 'rgba(255, 255, 255, 0.7)',
                              },
                            }}
                          />
                          <TextField
                            label="Address Line 2 (Optional)"
                            value={editedOrder?.shipping_address?.address2 || ''}
                            onChange={(e) => handleUpdateShippingAddress('address2', e.target.value)}
                            size="small"
                            fullWidth
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                                color: '#ffffff',
                                '& fieldset': {
                                  borderColor: 'rgba(255, 255, 255, 0.2)',
                                },
                                '&:hover fieldset': {
                                  borderColor: 'rgba(255, 255, 255, 0.3)',
                                },
                              },
                              '& .MuiInputLabel-root': {
                                color: 'rgba(255, 255, 255, 0.7)',
                              },
                            }}
                          />
                          <Grid container spacing={2}>
                            <Grid item xs={6}>
                              <TextField
                                label="City"
                                value={editedOrder?.shipping_address?.city || ''}
                                onChange={(e) => handleUpdateShippingAddress('city', e.target.value)}
                                size="small"
                                fullWidth
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.08)',
                                    color: '#ffffff',
                                    '& fieldset': {
                                      borderColor: 'rgba(255, 255, 255, 0.2)',
                                    },
                                    '&:hover fieldset': {
                                      borderColor: 'rgba(255, 255, 255, 0.3)',
                                    },
                                  },
                                  '& .MuiInputLabel-root': {
                                    color: 'rgba(255, 255, 255, 0.7)',
                                  },
                                }}
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <TextField
                                label="Postal Code"
                                value={editedOrder?.shipping_address?.zip || ''}
                                onChange={(e) => handleUpdateShippingAddress('zip', e.target.value)}
                                size="small"
                                fullWidth
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.08)',
                                    color: '#ffffff',
                                    '& fieldset': {
                                      borderColor: 'rgba(255, 255, 255, 0.2)',
                                    },
                                    '&:hover fieldset': {
                                      borderColor: 'rgba(255, 255, 255, 0.3)',
                                    },
                                  },
                                  '& .MuiInputLabel-root': {
                                    color: 'rgba(255, 255, 255, 0.7)',
                                  },
                                }}
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <TextField
                                label="Province/State"
                                value={editedOrder?.shipping_address?.province || ''}
                                onChange={(e) => handleUpdateShippingAddress('province', e.target.value)}
                                size="small"
                                fullWidth
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.08)',
                                    color: '#ffffff',
                                    '& fieldset': {
                                      borderColor: 'rgba(255, 255, 255, 0.2)',
                                    },
                                    '&:hover fieldset': {
                                      borderColor: 'rgba(255, 255, 255, 0.3)',
                                    },
                                  },
                                  '& .MuiInputLabel-root': {
                                    color: 'rgba(255, 255, 255, 0.7)',
                                  },
                                }}
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <TextField
                                label="Country"
                                value={editedOrder?.shipping_address?.country || ''}
                                onChange={(e) => handleUpdateShippingAddress('country', e.target.value)}
                                size="small"
                                fullWidth
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.08)',
                                    color: '#ffffff',
                                    '& fieldset': {
                                      borderColor: 'rgba(255, 255, 255, 0.2)',
                                    },
                                    '&:hover fieldset': {
                                      borderColor: 'rgba(255, 255, 255, 0.3)',
                                    },
                                  },
                                  '& .MuiInputLabel-root': {
                                    color: 'rgba(255, 255, 255, 0.7)',
                                  },
                                }}
                              />
                            </Grid>
                          </Grid>
                        </Box>
                      ) : (
                        selectedOrder.shipping_address ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 600 }}>
                              {selectedOrder.shipping_address.name}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                              {selectedOrder.shipping_address.address1}
                            </Typography>
                            {selectedOrder.shipping_address.address2 && (
                              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                                {selectedOrder.shipping_address.address2}
                              </Typography>
                            )}
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                              {selectedOrder.shipping_address.city}, {selectedOrder.shipping_address.province} {selectedOrder.shipping_address.zip}
                            </Typography>
                            <Chip
                              label={selectedOrder.shipping_address.country}
                              size="small"
                              sx={{ 
                                alignSelf: 'flex-start',
                                backgroundColor: 'rgba(59, 130, 246, 0.2)',
                                color: '#3b82f6',
                                border: '1px solid rgba(59, 130, 246, 0.3)',
                                fontWeight: 600
                              }}
                            />
                          </Box>
                        ) : (
                          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', fontStyle: 'italic' }}>
                            No shipping address provided
                          </Typography>
                        )
                      )}
                    </CardContent>
                  </Card>
                </Grid>
                
                {/* 📋 NEW: Order Updates Timeline - Always Show */}
                <Grid item xs={12}>
                  <Card className="glass-card">
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 600, mb: 3 }}>
                        Order Timeline
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', ml: 2 }}>
                          ({selectedOrder.order_updates?.length || 0} updates)
                        </Typography>
                      </Typography>
                      
                      {selectedOrder.order_updates && selectedOrder.order_updates.length > 0 ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 300, overflow: 'auto' }}>
                          {selectedOrder.order_updates
                            .slice()
                            .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                            .map((update, index) => (
                              <Box 
                                key={index} 
                                sx={{ 
                                  display: 'flex', 
                                  alignItems: 'flex-start', 
                                  gap: 2,
                                  p: 2,
                                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                  borderRadius: 2,
                                  borderLeft: `4px solid ${
                                    update.type === 'problem' ? '#ef4444' : 
                                    update.type === 'sent_to_fulfillment' ? '#22c55e' :
                                    update.type === 'imported' ? '#3b82f6' :
                                    update.type === 'status_changed' ? '#f59e0b' :
                                    update.type === 'tracking_updated' ? '#8b5cf6' :
                                    '#9ca3af'
                                  }`
                                }}
                              >
                                <Box sx={{ minWidth: 120, pt: 0.5 }}>
                                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontFamily: 'monospace' }}>
                                    {new Date(update.timestamp).toLocaleString('en-GB', {
                                      day: '2-digit',
                                      month: '2-digit',
                                      year: '2-digit',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                  </Typography>
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    <Chip
                                      label={update.type.replace('_', ' ').toUpperCase()}
                                      size="small"
                                      sx={{
                                        backgroundColor: update.type === 'problem' ? 'rgba(239, 68, 68, 0.2)' : 
                                                        update.type === 'sent_to_fulfillment' ? 'rgba(34, 197, 94, 0.2)' :
                                                        update.type === 'imported' ? 'rgba(59, 130, 246, 0.2)' :
                                                        update.type === 'status_changed' ? 'rgba(245, 158, 11, 0.2)' :
                                                        update.type === 'tracking_updated' ? 'rgba(139, 92, 246, 0.2)' :
                                                        'rgba(156, 163, 175, 0.2)',
                                        color: update.type === 'problem' ? '#fca5a5' : 
                                               update.type === 'sent_to_fulfillment' ? '#4ade80' :
                                               update.type === 'imported' ? '#60a5fa' :
                                               update.type === 'status_changed' ? '#fbbf24' :
                                               update.type === 'tracking_updated' ? '#c4b5fd' :
                                               '#d1d5db',
                                        fontWeight: 600,
                                        fontSize: '0.65rem'
                                      }}
                                    />
                                  </Box>
                                  <Typography variant="body2" sx={{ color: '#ffffff', fontWeight: 500, mb: 1 }}>
                                    {update.description}
                                  </Typography>
                                  {update.metadata && Object.keys(update.metadata).length > 0 && (
                                    <Box sx={{ pl: 2, borderLeft: '2px solid rgba(255,255,255,0.1)' }}>
                                      {Object.entries(update.metadata).map(([key, value]) => (
                                        <Typography 
                                          key={key} 
                                          variant="caption" 
                                          sx={{ 
                                            color: 'rgba(255,255,255,0.7)', 
                                            display: 'block',
                                            fontSize: '0.75rem'
                                          }}
                                        >
                                          <strong>{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:</strong> {
                                            typeof value === 'object' ? JSON.stringify(value) : String(value)
                                          }
                                        </Typography>
                                      ))}
                                    </Box>
                                  )}
                                </Box>
                              </Box>
                            ))}
                        </Box>
                      ) : (
                        /* Empty State */
                        <Box sx={{ 
                          textAlign: 'center', 
                          py: 4,
                          backgroundColor: 'rgba(255, 255, 255, 0.02)',
                          borderRadius: 2,
                          border: '1px dashed rgba(255, 255, 255, 0.1)'
                        }}>
                          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mb: 2 }}>
                            📋 No timeline updates available
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                            Order updates will appear here as the order progresses through fulfillment
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                {/* 📦 NEW: Enhanced Tracking Information */}
                {(trackingInfo || trackingLoading) && (
                  <Grid item xs={12}>
                    <Card className="glass-card">
                      <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                          <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 600 }}>
                            Tracking Information
                            {trackingLoading && (
                              <CircularProgress size={16} sx={{ ml: 2, color: '#fbbf24' }} />
                            )}
                          </Typography>
                          <Button
                            variant="outlined"
                            size="small"
                            disabled={trackingLoading}
                            onClick={() => fetchEnhancedTracking(selectedOrder.id)}
                            sx={{
                              borderColor: 'rgba(245, 158, 11, 0.3)',
                              color: '#fbbf24',
                              '&:hover': {
                                borderColor: 'rgba(245, 158, 11, 0.5)',
                                backgroundColor: 'rgba(245, 158, 11, 0.1)'
                              }
                            }}
                          >
                            🔄 Refresh
                          </Button>
                        </Box>
                        
                        {trackingLoading ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                              Refreshing tracking information...
                            </Typography>
                          </Box>
                        ) : trackingInfo ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            
                            {/* Tracking Refresh Status */}
                            {trackingInfo.tracking_refresh && (
                              <Box className="glass" sx={{ p: 2.5, borderRadius: 2 }}>
                                <Typography variant="subtitle1" sx={{ color: '#ffffff', fontWeight: 600, mb: 2 }}>
                                  Yuntrack Status
                                </Typography>
                                {trackingInfo.tracking_refresh.success ? (
                                  <Box>
                                    <Chip
                                      label="✓ Ready"
                                      size="small"
                                      sx={{
                                        backgroundColor: 'rgba(34, 197, 94, 0.15)',
                                        color: '#4ade80',
                                        border: '1px solid rgba(34, 197, 94, 0.3)',
                                        fontWeight: 600,
                                        mb: 1
                                      }}
                                    />
                                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', mt: 1 }}>
                                      {trackingInfo.tracking_refresh.message}
                                    </Typography>
                                    {trackingInfo.tracking_refresh.new_tracking && (
                                      <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="body2" sx={{ color: '#4ade80', fontFamily: 'monospace' }}>
                                          {trackingInfo.tracking_refresh.new_tracking}
                                        </Typography>
                                        {trackingInfo.tracking_refresh.yuntrack_url && (
                                          <Button
                                            variant="contained"
                                            size="small"
                                            sx={{
                                              backgroundColor: 'rgba(245, 158, 11, 0.2)',
                                              color: '#fbbf24',
                                              border: '1px solid rgba(245, 158, 11, 0.4)',
                                              '&:hover': {
                                                backgroundColor: 'rgba(245, 158, 11, 0.3)',
                                              }
                                            }}
                                            onClick={() => {
                                              window.open(trackingInfo.tracking_refresh.yuntrack_url, '_blank');
                                            }}
                                            title="Track on Yuntrack website"
                                          >
                                            Track on Yuntrack 🔗
                                          </Button>
                                        )}
                                      </Box>
                                    )}
                                  </Box>
                                ) : (
                                  <Box>
                                    <Chip
                                      label="⚠ Error"
                                      size="small"
                                      sx={{
                                        backgroundColor: 'rgba(239, 68, 68, 0.15)',
                                        color: '#fca5a5',
                                        border: '1px solid rgba(239, 68, 68, 0.3)',
                                        fontWeight: 600,
                                        mb: 1
                                      }}
                                    />
                                    <Typography variant="body2" sx={{ color: '#fca5a5', mt: 1 }}>
                                      {trackingInfo.tracking_refresh.error}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            )}

                            {/* Carrier Tracking - Only for BRT orders */}
                            {trackingInfo.carrier_tracking && trackingInfo.carrier_tracking.carrier === 'BRT' && (
                              <Box className="glass" sx={{ p: 2.5, borderRadius: 2 }}>
                                <Typography variant="subtitle1" sx={{ color: '#ffffff', fontWeight: 600, mb: 2 }}>
                                  BRT Tracking
                                </Typography>
                                
                                {trackingInfo.carrier_tracking.success ? (
                                  <Box>
                                    <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                                      <Chip
                                        label={trackingInfo.carrier_tracking.status || trackingInfo.carrier_tracking.latestStatus || 'In Transit'}
                                        size="small"
                                        sx={{
                                          backgroundColor: trackingInfo.carrier_tracking.isDelivered ? 'rgba(34, 197, 94, 0.15)' : 'rgba(99, 102, 241, 0.15)',
                                          color: trackingInfo.carrier_tracking.isDelivered ? '#4ade80' : '#a5b4fc',
                                          border: trackingInfo.carrier_tracking.isDelivered ? '1px solid rgba(34, 197, 94, 0.3)' : '1px solid rgba(99, 102, 241, 0.3)',
                                          fontWeight: 600
                                        }}
                                      />
                                      <Chip
                                        label="BRT"
                                        size="small"
                                        sx={{
                                          backgroundColor: 'rgba(156, 163, 175, 0.15)',
                                          color: '#9ca3af',
                                          border: '1px solid rgba(156, 163, 175, 0.3)',
                                          fontWeight: 600
                                        }}
                                      />
                                    </Box>
                                    
                                    {/* Display tracking events for BRT */}
                                    {trackingInfo.carrier_tracking.events && (
                                      <Box>
                                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', mb: 2, fontWeight: 600 }}>
                                          Tracking History:
                                        </Typography>
                                        <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                                          {trackingInfo.carrier_tracking.events.map((event, index) => (
                                            <Box key={index} sx={{ 
                                              mb: 1.5, 
                                              pb: 1.5, 
                                              borderBottom: index < trackingInfo.carrier_tracking.events.length - 1 ? '1px solid rgba(255,255,255,0.1)' : 'none'
                                            }}>
                                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                                                <Typography variant="body2" sx={{ color: '#ffffff', fontWeight: 600 }}>
                                                  {event.event || event.status}
                                                </Typography>
                                                {event.date && (
                                                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontFamily: 'monospace' }}>
                                                    {event.date} {event.time}
                                                  </Typography>
                                                )}
                                              </Box>
                                              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                                                📍 {event.location}
                                              </Typography>
                                            </Box>
                                          ))}
                                        </Box>
                                      </Box>
                                    )}
                                  </Box>
                                ) : (
                                  <Box>
                                    <Chip
                                      label="No tracking data"
                                      size="small"
                                      sx={{
                                        backgroundColor: 'rgba(156, 163, 175, 0.15)',
                                        color: '#9ca3af',
                                        border: '1px solid rgba(156, 163, 175, 0.3)',
                                        fontWeight: 600,
                                        mb: 1
                                      }}
                                    />
                                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', mt: 1 }}>
                                      {trackingInfo.carrier_tracking.message || trackingInfo.carrier_tracking.error || 'No tracking information available'}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            )}
                          </Box>
                        ) : null}
                      </CardContent>
                    </Card>
                  </Grid>
                )}
                
                {(selectedOrder.line_items || editMode) && (
                  <Grid item xs={12}>
                    <Card className="glass-card">
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                          <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 600 }}>
                            Order Items ({editMode ? (editedOrder?.line_items?.length || 0) : (selectedOrder.line_items?.length || 0)})
                            {editMode && (
                              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', ml: 2 }}>
                                (Editable)
                              </Typography>
                            )}
                          </Typography>
                          {editMode && (
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<Edit />}
                              onClick={handleAddLineItem}
                              sx={{
                                borderColor: 'rgba(34, 197, 94, 0.3)',
                                color: '#4ade80',
                                '&:hover': {
                                  borderColor: 'rgba(34, 197, 94, 0.5)',
                                  backgroundColor: 'rgba(34, 197, 94, 0.1)'
                                }
                              }}
                            >
                              Add Product
                            </Button>
                          )}
                        </Box>
                        
                        {/* Order Pricing Breakdown in Edit Mode */}
                        {editMode && editedOrder?.line_items && editedOrder.line_items.length > 0 && (
                          <Box sx={{ 
                            mb: 3, 
                            p: 3, 
                            backgroundColor: 'rgba(34, 197, 94, 0.1)', 
                            borderRadius: 2,
                            border: '1px solid rgba(34, 197, 94, 0.3)'
                          }}>
                            <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 600, mb: 2 }}>
                              Order Pricing Breakdown
                            </Typography>
                            
                            {/* Calculate totals */}
                            {(() => {
                              const subtotal = editedOrder.line_items.reduce((total, item) => 
                                total + (parseFloat(item.final_price || item.price || 0) * parseInt(item.quantity || 1)), 0
                              );
                              const shippingAmount = parseFloat(editedOrder.shipping_amount || 0);
                              const taxAmount = parseFloat(editedOrder.tax_amount || 0);
                              const orderDiscountAmount = parseFloat(editedOrder.order_discount_amount || 0);
                              const finalTotal = subtotal + shippingAmount + taxAmount - orderDiscountAmount;
                              
                              return (
                                <Grid container spacing={2}>
                                  {/* Order-level adjustments */}
                                  <Grid item xs={12} md={8}>
                                    <Grid container spacing={2}>
                                      <Grid item xs={6}>
                                        <TextField
                                          label="Shipping Amount (€)"
                                          type="number"
                                          value={editedOrder.shipping_amount || '0.00'}
                                          onChange={(e) => {
                                            const newShipping = e.target.value;
                                            setEditedOrder(prev => ({ ...prev, shipping_amount: newShipping }));
                                          }}
                                          size="small"
                                          fullWidth
                                          inputProps={{ min: 0, step: 0.01 }}
                                          sx={{
                                            '& .MuiOutlinedInput-root': {
                                              backgroundColor: 'rgba(99, 102, 241, 0.08)',
                                              color: '#ffffff',
                                              '& fieldset': {
                                                borderColor: 'rgba(99, 102, 241, 0.3)',
                                              },
                                              '&:hover fieldset': {
                                                borderColor: 'rgba(99, 102, 241, 0.5)',
                                              },
                                            },
                                            '& .MuiInputLabel-root': {
                                              color: 'rgba(99, 102, 241, 0.9)',
                                            },
                                          }}
                                        />
                                      </Grid>
                                      <Grid item xs={6}>
                                        <TextField
                                          label="Tax Amount (€)"
                                          type="number"
                                          value={editedOrder.tax_amount || '0.00'}
                                          onChange={(e) => {
                                            const newTax = e.target.value;
                                            setEditedOrder(prev => ({ ...prev, tax_amount: newTax }));
                                          }}
                                          size="small"
                                          fullWidth
                                          inputProps={{ min: 0, step: 0.01 }}
                                          sx={{
                                            '& .MuiOutlinedInput-root': {
                                              backgroundColor: 'rgba(156, 163, 175, 0.08)',
                                              color: '#ffffff',
                                              '& fieldset': {
                                                borderColor: 'rgba(156, 163, 175, 0.3)',
                                              },
                                              '&:hover fieldset': {
                                                borderColor: 'rgba(156, 163, 175, 0.5)',
                                              },
                                            },
                                            '& .MuiInputLabel-root': {
                                              color: 'rgba(156, 163, 175, 0.9)',
                                            },
                                          }}
                                        />
                                      </Grid>
                                      <Grid item xs={12}>
                                        <TextField
                                          label="Order Discount (€)"
                                          type="number"
                                          value={editedOrder.order_discount_amount || '0.00'}
                                          onChange={(e) => {
                                            const newDiscount = e.target.value;
                                            setEditedOrder(prev => ({ ...prev, order_discount_amount: newDiscount }));
                                          }}
                                          size="small"
                                          fullWidth
                                          inputProps={{ min: 0, step: 0.01 }}
                                          helperText="Discount applied to entire order"
                                          sx={{
                                            '& .MuiOutlinedInput-root': {
                                              backgroundColor: 'rgba(239, 68, 68, 0.08)',
                                              color: '#ffffff',
                                              '& fieldset': {
                                                borderColor: 'rgba(239, 68, 68, 0.3)',
                                              },
                                              '&:hover fieldset': {
                                                borderColor: 'rgba(239, 68, 68, 0.5)',
                                              },
                                            },
                                            '& .MuiInputLabel-root': {
                                              color: 'rgba(239, 68, 68, 0.9)',
                                            },
                                            '& .MuiFormHelperText-root': {
                                              color: 'rgba(239, 68, 68, 0.7)',
                                              fontSize: '0.7rem'
                                            },
                                          }}
                                        />
                                      </Grid>
                                    </Grid>
                                  </Grid>
                                  
                                  {/* Totals breakdown */}
                                  <Grid item xs={12} md={4}>
                                    <Box sx={{ 
                                      p: 2, 
                                      backgroundColor: 'rgba(255, 255, 255, 0.05)', 
                                      borderRadius: 1,
                                      border: '1px solid rgba(255, 255, 255, 0.1)'
                                    }}>
                                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                                          Items Subtotal:
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#ffffff', fontWeight: 600 }}>
                                          €{subtotal.toFixed(2)}
                                        </Typography>
                                      </Box>
                                      
                                      {shippingAmount > 0 && (
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                          <Typography variant="body2" sx={{ color: 'rgba(99, 102, 241, 0.9)' }}>
                                            Shipping:
                                          </Typography>
                                          <Typography variant="body2" sx={{ color: 'rgba(99, 102, 241, 0.9)', fontWeight: 600 }}>
                                            €{shippingAmount.toFixed(2)}
                                          </Typography>
                                        </Box>
                                      )}
                                      
                                      {taxAmount > 0 && (
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                          <Typography variant="body2" sx={{ color: 'rgba(156, 163, 175, 0.9)' }}>
                                            Tax:
                                          </Typography>
                                          <Typography variant="body2" sx={{ color: 'rgba(156, 163, 175, 0.9)', fontWeight: 600 }}>
                                            €{taxAmount.toFixed(2)}
                                          </Typography>
                                        </Box>
                                      )}
                                      
                                      {orderDiscountAmount > 0 && (
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                          <Typography variant="body2" sx={{ color: 'rgba(239, 68, 68, 0.9)' }}>
                                            Order Discount:
                                          </Typography>
                                          <Typography variant="body2" sx={{ color: 'rgba(239, 68, 68, 0.9)', fontWeight: 600 }}>
                                            -€{orderDiscountAmount.toFixed(2)}
                                          </Typography>
                                        </Box>
                                      )}
                                      
                                      <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, borderTop: '1px solid rgba(255,255,255,0.2)' }}>
                                        <Typography variant="h6" sx={{ color: '#22c55e', fontWeight: 700 }}>
                                          Order Total:
                                        </Typography>
                                        <Typography variant="h6" sx={{ color: '#22c55e', fontWeight: 700 }}>
                                          €{finalTotal.toFixed(2)}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  </Grid>
                                </Grid>
                              );
                            })()}
                          </Box>
                        )}
                        
                        <Grid container spacing={2}>
                          {(editMode ? (editedOrder?.line_items || []) : (selectedOrder.line_items || [])).map((item, index) => (
                            <Grid item xs={12} key={index}>
                              <Box 
                                className="glass" 
                                sx={{ 
                                  p: 2.5, 
                                  borderRadius: 2,
                                  transition: 'all 0.3s ease',
                                  '&:hover': {
                                    transform: 'translateY(-1px)',
                                    boxShadow: '0 8px 25px rgba(0,0,0,0.3)'
                                  }
                                }}
                              >
                                {editMode ? (
                                  /* EDIT MODE - Editable Fields */
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                      <Typography variant="subtitle1" sx={{ color: '#ffffff', fontWeight: 600 }}>
                                        Product #{index + 1}
                                      </Typography>
                                      {(editedOrder?.line_items?.length || 0) > 1 && (
                                        <Button
                                          size="small"
                                          color="error"
                                          startIcon={<Delete />}
                                          onClick={() => handleRemoveLineItem(index)}
                                          sx={{ minWidth: 'auto' }}
                                        >
                                          Remove
                                        </Button>
                                      )}
                                    </Box>
                                    
                                    <Grid container spacing={2}>
                                                                             <Grid item xs={12} md={6}>
                                         <TextField
                                           label="Product Title"
                                           value={item.title || ''}
                                           onChange={(e) => handleUpdateLineItem(index, 'title', e.target.value)}
                                           size="small"
                                           fullWidth
                                           helperText="Auto-populates when valid SKU is selected"
                                           sx={{
                                             '& .MuiOutlinedInput-root': {
                                               backgroundColor: 'rgba(255, 255, 255, 0.08)',
                                               color: '#ffffff',
                                               '& fieldset': {
                                                 borderColor: 'rgba(255, 255, 255, 0.2)',
                                               },
                                               '&:hover fieldset': {
                                                 borderColor: 'rgba(255, 255, 255, 0.3)',
                                               },
                                             },
                                             '& .MuiInputLabel-root': {
                                               color: 'rgba(255, 255, 255, 0.7)',
                                             },
                                             '& .MuiFormHelperText-root': {
                                               color: 'rgba(255, 255, 255, 0.5)',
                                               fontSize: '0.7rem'
                                             },
                                           }}
                                         />
                                       </Grid>
                                                                             <Grid item xs={12} md={6}>
                                         <Autocomplete
                                           size="small"
                                           options={availableSkus}
                                           value={item.sku || ''}
                                           onChange={(event, newValue) => handleSkuSelect(index, newValue || '')}
                                           loading={loadingSkus}
                                           freeSolo
                                           clearOnEscape
                                           renderInput={(params) => (
                                             <TextField
                                               {...params}
                                               label="SKU"
                                               placeholder="Select or type SKU..."
                                               sx={{
                                                 '& .MuiOutlinedInput-root': {
                                                   backgroundColor: 'rgba(255, 255, 255, 0.08)',
                                                   color: '#ffffff',
                                                   '& fieldset': {
                                                     borderColor: 'rgba(255, 255, 255, 0.2)',
                                                   },
                                                   '&:hover fieldset': {
                                                     borderColor: 'rgba(255, 255, 255, 0.3)',
                                                   },
                                                 },
                                                 '& .MuiInputLabel-root': {
                                                   color: 'rgba(255, 255, 255, 0.7)',
                                                 },
                                                 '& .MuiAutocomplete-endAdornment': {
                                                   '& .MuiSvgIcon-root': {
                                                     color: 'rgba(255, 255, 255, 0.7)',
                                                   },
                                                 },
                                               }}
                                               InputProps={{
                                                 ...params.InputProps,
                                                 endAdornment: (
                                                   <>
                                                     {loadingSkus ? <CircularProgress color="inherit" size={20} /> : null}
                                                     {params.InputProps.endAdornment}
                                                   </>
                                                 ),
                                               }}
                                             />
                                           )}
                                           noOptionsText="No SKUs available - type to add custom"
                                         />
                                       </Grid>
                                                                             <Grid item xs={6} md={2}>
                                         <TextField
                                           label="Quantity"
                                           type="number"
                                           value={item.quantity || 1}
                                           onChange={(e) => handleUpdateLineItem(index, 'quantity', parseInt(e.target.value) || 1)}
                                           size="small"
                                           fullWidth
                                           inputProps={{ min: 1 }}
                                           sx={{
                                             '& .MuiOutlinedInput-root': {
                                               backgroundColor: 'rgba(255, 255, 255, 0.08)',
                                               color: '#ffffff',
                                               '& fieldset': {
                                                 borderColor: 'rgba(255, 255, 255, 0.2)',
                                               },
                                               '&:hover fieldset': {
                                                 borderColor: 'rgba(255, 255, 255, 0.3)',
                                               },
                                             },
                                             '& .MuiInputLabel-root': {
                                               color: 'rgba(255, 255, 255, 0.7)',
                                             },
                                           }}
                                         />
                                       </Grid>
                                       <Grid item xs={6} md={2.5}>
                                         <TextField
                                           label="Original Price (€)"
                                           type="number"
                                           value={item.original_price || '0.00'}
                                           onChange={(e) => handleUpdateLineItem(index, 'original_price', e.target.value)}
                                           size="small"
                                           fullWidth
                                           inputProps={{ min: 0, step: 0.01 }}
                                           helperText="Base price before discount"
                                           sx={{
                                             '& .MuiOutlinedInput-root': {
                                               backgroundColor: 'rgba(255, 255, 255, 0.08)',
                                               color: '#ffffff',
                                               '& fieldset': {
                                                 borderColor: 'rgba(255, 255, 255, 0.2)',
                                               },
                                               '&:hover fieldset': {
                                                 borderColor: 'rgba(255, 255, 255, 0.3)',
                                               },
                                             },
                                             '& .MuiInputLabel-root': {
                                               color: 'rgba(255, 255, 255, 0.7)',
                                             },
                                             '& .MuiFormHelperText-root': {
                                               color: 'rgba(255, 255, 255, 0.5)',
                                               fontSize: '0.7rem'
                                             },
                                           }}
                                         />
                                       </Grid>
                                       <Grid item xs={6} md={2.5}>
                                         <TextField
                                           label="Discount %"
                                           type="number"
                                           value={item.discount_percentage || '0'}
                                           onChange={(e) => handleUpdateLineItem(index, 'discount_percentage', e.target.value)}
                                           size="small"
                                           fullWidth
                                           inputProps={{ min: 0, max: 100, step: 0.1 }}
                                           helperText="Auto-calculates amount"
                                           sx={{
                                             '& .MuiOutlinedInput-root': {
                                               backgroundColor: 'rgba(245, 158, 11, 0.08)',
                                               color: '#ffffff',
                                               '& fieldset': {
                                                 borderColor: 'rgba(245, 158, 11, 0.3)',
                                               },
                                               '&:hover fieldset': {
                                                 borderColor: 'rgba(245, 158, 11, 0.5)',
                                               },
                                             },
                                             '& .MuiInputLabel-root': {
                                               color: 'rgba(245, 158, 11, 0.9)',
                                             },
                                             '& .MuiFormHelperText-root': {
                                               color: 'rgba(245, 158, 11, 0.7)',
                                               fontSize: '0.7rem'
                                             },
                                           }}
                                         />
                                       </Grid>
                                       <Grid item xs={6} md={2.5}>
                                         <TextField
                                           label="Discount (€)"
                                           type="number"
                                           value={item.discount_amount || '0.00'}
                                           onChange={(e) => handleUpdateLineItem(index, 'discount_amount', e.target.value)}
                                           size="small"
                                           fullWidth
                                           inputProps={{ min: 0, step: 0.01 }}
                                           helperText="Fixed amount discount"
                                           sx={{
                                             '& .MuiOutlinedInput-root': {
                                               backgroundColor: 'rgba(239, 68, 68, 0.08)',
                                               color: '#ffffff',
                                               '& fieldset': {
                                                 borderColor: 'rgba(239, 68, 68, 0.3)',
                                               },
                                               '&:hover fieldset': {
                                                 borderColor: 'rgba(239, 68, 68, 0.5)',
                                               },
                                             },
                                             '& .MuiInputLabel-root': {
                                               color: 'rgba(239, 68, 68, 0.9)',
                                             },
                                             '& .MuiFormHelperText-root': {
                                               color: 'rgba(239, 68, 68, 0.7)',
                                               fontSize: '0.7rem'
                                             },
                                           }}
                                         />
                                       </Grid>
                                       <Grid item xs={12} md={2.5}>
                                         <TextField
                                           label="Final Price (€)"
                                           type="number"
                                           value={item.final_price || '0.00'}
                                           size="small"
                                           fullWidth
                                           disabled
                                           helperText="Auto-calculated"
                                           sx={{
                                             '& .MuiOutlinedInput-root': {
                                               backgroundColor: 'rgba(34, 197, 94, 0.08)',
                                               color: '#4ade80',
                                               '& fieldset': {
                                                 borderColor: 'rgba(34, 197, 94, 0.3)',
                                               },
                                             },
                                             '& .MuiInputLabel-root': {
                                               color: 'rgba(34, 197, 94, 0.9)',
                                             },
                                             '& .MuiFormHelperText-root': {
                                               color: 'rgba(34, 197, 94, 0.7)',
                                               fontSize: '0.7rem'
                                             },
                                           }}
                                         />
                                       </Grid>
                                      <Grid item xs={12}>
                                        <TextField
                                          label="Variant Title (Optional)"
                                          value={item.variant_title || ''}
                                          onChange={(e) => handleUpdateLineItem(index, 'variant_title', e.target.value)}
                                          size="small"
                                          fullWidth
                                          sx={{
                                            '& .MuiOutlinedInput-root': {
                                              backgroundColor: 'rgba(255, 255, 255, 0.08)',
                                              color: '#ffffff',
                                              '& fieldset': {
                                                borderColor: 'rgba(255, 255, 255, 0.2)',
                                              },
                                              '&:hover fieldset': {
                                                borderColor: 'rgba(255, 255, 255, 0.3)',
                                              },
                                            },
                                            '& .MuiInputLabel-root': {
                                              color: 'rgba(255, 255, 255, 0.7)',
                                            },
                                          }}
                                        />
                                      </Grid>
                                    </Grid>
                                    
                                    <Box sx={{ textAlign: 'right', pt: 1, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                        {(parseFloat(item.original_price || 0) !== parseFloat(item.final_price || 0)) && (
                                          <Typography variant="caption" sx={{ color: 'rgba(239, 68, 68, 0.8)', textDecoration: 'line-through' }}>
                                            Original: €{(parseFloat(item.original_price || 0) * parseInt(item.quantity || 1)).toFixed(2)}
                                          </Typography>
                                        )}
                                        <Typography variant="body1" sx={{ color: '#22c55e', fontWeight: 700 }}>
                                          Line Total: €{(parseFloat(item.final_price || item.price || 0) * parseInt(item.quantity || 1)).toFixed(2)}
                                        </Typography>
                                        {(parseFloat(item.discount_amount || 0) > 0 || parseFloat(item.discount_percentage || 0) > 0) && (
                                          <Typography variant="caption" sx={{ color: 'rgba(245, 158, 11, 0.8)' }}>
                                            Discount: -€{((parseFloat(item.original_price || 0) - parseFloat(item.final_price || 0)) * parseInt(item.quantity || 1)).toFixed(2)}
                                          </Typography>
                                        )}
                                      </Box>
                                    </Box>
                                  </Box>
                                ) : (
                                  /* VIEW MODE - Display Only */
                                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                    {/* Product Image */}
                                    <ProductImage sku={item.sku} title={item.title} />
                                    
                                    {/* Product Details */}
                                    <Box sx={{ flex: 1 }}>
                                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                                        <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 600, fontSize: '1.1rem' }}>
                                          {item.title}
                                        </Typography>
                                        <Typography variant="h6" sx={{ color: '#22c55e', fontWeight: 700 }}>
                                          €{parseFloat(item.price || 0).toFixed(2)}
                                        </Typography>
                                      </Box>
                                      
                                      {item.variant_title && (
                                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', mb: 1 }}>
                                          Variant: {item.variant_title}
                                        </Typography>
                                      )}
                                      
                                      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase', fontWeight: 600 }}>
                                            SKU:
                                          </Typography>
                                          <Chip 
                                            label={item.sku || 'N/A'} 
                                            size="small" 
                                            sx={{ 
                                              backgroundColor: 'rgba(99, 102, 241, 0.2)',
                                              color: '#a5b4fc',
                                              border: '1px solid rgba(99, 102, 241, 0.3)',
                                              fontFamily: 'monospace',
                                              fontWeight: 600
                                            }} 
                                          />
                                        </Box>
                                        
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase', fontWeight: 600 }}>
                                            Quantity:
                                          </Typography>
                                          <Chip 
                                            label={item.quantity} 
                                            size="small"
                                            sx={{ 
                                              backgroundColor: 'rgba(34, 197, 94, 0.2)',
                                              color: '#4ade80',
                                              border: '1px solid rgba(34, 197, 94, 0.3)',
                                              fontWeight: 700
                                            }}
                                          />
                                        </Box>
                                        
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase', fontWeight: 600 }}>
                                            Total:
                                          </Typography>
                                          <Typography variant="body2" sx={{ color: '#22c55e', fontWeight: 700 }}>
                                            €{(parseFloat(item.price || 0) * parseInt(item.quantity || 0)).toFixed(2)}
                                          </Typography>
                                        </Box>
                                      </Box>
                                    </Box>
                                  </Box>
                                )}
                              </Box>
                            </Grid>
                          ))}
                          
                          {/* Show message if no items */}
                          {(!selectedOrder.line_items || selectedOrder.line_items.length === 0) && !editMode && (
                            <Grid item xs={12}>
                              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', fontStyle: 'italic', textAlign: 'center', py: 2 }}>
                                No items in this order
                              </Typography>
                            </Grid>
                          )}
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {/* Edit Mode Actions */}
          {editMode && (
            <>
              <Button
                onClick={handleCancelEdit}
                color="secondary"
              >
                Cancel Edit
              </Button>
              <Button
                startIcon={savingOrder ? <CircularProgress size={20} /> : <CheckCircle />}
                onClick={handleSaveOrder}
                variant="contained"
                disabled={savingOrder}
                sx={{
                  background: 'linear-gradient(145deg, rgba(34, 197, 94, 0.9), rgba(22, 163, 74, 0.9))',
                  '&:hover': {
                    background: 'linear-gradient(145deg, rgba(34, 197, 94, 1), rgba(22, 163, 74, 1))',
                  }
                }}
              >
                {savingOrder ? 'Saving...' : 'Save Changes'}
              </Button>
            </>
          )}
          
          {/* View Mode Actions */}
          {!editMode && (
            <>
                             {/* Edit Button - Only for orders that haven't been sent */}
               {isUser() && selectedOrder && ['pending', 'Problem'].includes(selectedOrder.status) && (
                 <Button
                   startIcon={<Edit />}
                   onClick={handleStartEditing}
                   variant="outlined"
                   sx={{
                     borderColor: 'rgba(99, 102, 241, 0.3)',
                     color: '#a5b4fc',
                     '&:hover': {
                       borderColor: 'rgba(99, 102, 241, 0.5)',
                       backgroundColor: 'rgba(99, 102, 241, 0.1)'
                     }
                   }}
                 >
                   Edit Order
                 </Button>
               )}
              
              {/* Retry Button */}
              {isUser() && selectedOrder?.status === 'failed' && (
                <Button
                  startIcon={<RestartAlt />}
                  onClick={() => {
                    handleRetryOrder(selectedOrder.id);
                    setDialogOpen(false);
                  }}
                  color="warning"
                >
                  Retry Order
                </Button>
              )}
              
              {/* Cancel Order Button */}
              {isUser() && ['pending', 'processing', 'Problem'].includes(selectedOrder?.status) && (
                <Button
                  startIcon={<Cancel />}
                  onClick={() => {
                    handleCancelOrder(selectedOrder.id);
                    setDialogOpen(false);
                  }}
                  color="error"
                >
                  Cancel Order
                </Button>
              )}
            </>
          )}
          
          {/* Close Button */}
          <Button onClick={() => {
            setDialogOpen(false);
            setEditMode(false);
            setEditedOrder(null);
          }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Status Update Dialog */}
      <Dialog 
        open={bulkStatusDialogOpen} 
        onClose={() => {
          setBulkStatusDialogOpen(false);
          setBulkStatusValue('');
        }} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          Update Status for {selectedRows.length} Orders
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>New Status</InputLabel>
              <Select
                value={bulkStatusValue}
                onChange={(e) => setBulkStatusValue(e.target.value)}
                label="New Status"
              >
                <MenuItem value="Pending">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircle sx={{ color: '#f59e0b' }} />
                    Pending
                  </Box>
                </MenuItem>
                <MenuItem value="Problem">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Block sx={{ color: '#ef4444' }} />
                    Problem
                  </Box>
                </MenuItem>
                <MenuItem value="Sent">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocalShipping sx={{ color: '#3b82f6' }} />
                    Sent
                  </Box>
                </MenuItem>
                <MenuItem value="Shipped">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircle sx={{ color: '#22c55e' }} />
                    Shipped
                  </Box>
                </MenuItem>
                <MenuItem value="Cancelled">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Block sx={{ color: '#ef4444' }} />
                    Cancelled
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setBulkStatusDialogOpen(false);
              setBulkStatusValue('');
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleBulkStatusUpdate}
            variant="contained"
            disabled={!bulkStatusValue}
          >
            Update Status
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Fulfillment Center Change Dialog */}
      <Dialog 
        open={bulkFulfillmentDialogOpen} 
        onClose={() => {
          setBulkFulfillmentDialogOpen(false);
          setBulkFulfillmentCenterId('');
        }} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          Change Fulfillment Center for {selectedRows.length} Orders
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Fulfillment Center</InputLabel>
              <Select
                value={bulkFulfillmentCenterId}
                onChange={(e) => setBulkFulfillmentCenterId(e.target.value)}
                label="Fulfillment Center"
                disabled={changingFulfillmentCenter}
              >
                <MenuItem value="">
                  <em>Unassigned</em>
                </MenuItem>
                {fulfillmentCenters.map((center) => (
                  <MenuItem key={center.id} value={center.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box 
                        sx={{ 
                          width: 12, 
                          height: 12, 
                          borderRadius: '50%', 
                          backgroundColor: center.code === 'metakocka' ? '#22c55e' : '#3b82f6'
                        }} 
                      />
                      {center.name}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setBulkFulfillmentDialogOpen(false);
              setBulkFulfillmentCenterId('');
            }}
            disabled={changingFulfillmentCenter}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleBulkFulfillmentChange} 
            variant="contained"
            disabled={changingFulfillmentCenter || !bulkFulfillmentCenterId}
            startIcon={changingFulfillmentCenter ? <CircularProgress size={20} /> : null}
          >
            {changingFulfillmentCenter ? 'Updating...' : 'Update Fulfillment Center'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manual Sync Dialog */}
      <Dialog open={syncDialogOpen} onClose={() => setSyncDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Manual Order Sync from Shopify
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="number"
                  label="Days Ago"
                  value={syncOptions.daysAgo}
                  onChange={(e) => setSyncOptions(prev => ({ ...prev, daysAgo: parseInt(e.target.value) || 1 }))}
                  helperText="Number of days ago to sync orders from"
                  inputProps={{ min: 1, max: 30 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Limit"
                  value={syncOptions.limit}
                  onChange={(e) => setSyncOptions(prev => ({ ...prev, limit: parseInt(e.target.value) || 250 }))}
                  helperText="Maximum number of orders to fetch"
                  inputProps={{ min: 1, max: 250 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Include Fulfilled Orders</InputLabel>
                  <Select
                    value={syncOptions.includeFulfilled}
                    onChange={(e) => setSyncOptions(prev => ({ ...prev, includeFulfilled: e.target.value }))}
                    label="Include Fulfilled Orders"
                  >
                    <MenuItem value={false}>No - Only Unfulfilled</MenuItem>
                    <MenuItem value={true}>Yes - Include All</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Alert severity="info">
                  <strong>Debug Mode:</strong> Fulfillment processing is disabled. Orders will only be saved to the database.
                </Alert>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSyncDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleManualSync}
            variant="contained"
            disabled={syncLoading}
          >
            {syncLoading ? 'Syncing...' : 'Start Sync'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Tracking Sync Results Dialog */}
      <Dialog 
        open={trackingSyncDialogOpen} 
        onClose={() => setTrackingSyncDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocalShipping sx={{ color: '#f59e0b' }} />
            Tracking Sync Results
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {trackingSyncResults && (
              <>
                {trackingSyncResults.success ? (
                  <Alert severity="success" sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Sync Completed Successfully!
                    </Typography>
                    <Typography variant="body2">
                      {trackingSyncResults.message}
                    </Typography>
                  </Alert>
                ) : (
                  <Alert severity="error" sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Sync Failed
                    </Typography>
                    <Typography variant="body2">
                      {trackingSyncResults.message}
                    </Typography>
                  </Alert>
                )}

                {trackingSyncResults.success && (
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={4}>
                      <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#f3f4f6' }}>
                        <Typography variant="h4" sx={{ color: '#3b82f6', fontWeight: 700 }}>
                          {trackingSyncResults.checkedCount || 0}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6b7280' }}>
                          Orders Checked
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={4}>
                      <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#f0fdf4' }}>
                        <Typography variant="h4" sx={{ color: '#22c55e', fontWeight: 700 }}>
                          {trackingSyncResults.updatedCount || 0}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#16a34a' }}>
                          Tracking Updated
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={4}>
                      <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#fef3c7' }}>
                        <Typography variant="h4" sx={{ color: '#d97706', fontWeight: 700 }}>
                          {(trackingSyncResults.checkedCount || 0) - (trackingSyncResults.updatedCount || 0)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#b45309' }}>
                          No Changes
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                )}

                {trackingSyncResults.success && trackingSyncResults.details && trackingSyncResults.details.length > 0 && (
                  <Box>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      Updated Orders:
                    </Typography>
                    <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                      {trackingSyncResults.details.map((order, index) => (
                        <Paper key={index} sx={{ p: 2, mb: 1, backgroundColor: '#f8fafc' }}>
                          <Grid container spacing={2} alignItems="center">
                            <Grid item xs={3}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                Order #{order.shopify_order_number}
                              </Typography>
                            </Grid>
                            <Grid item xs={4}>
                              <Chip
                                label={order.new_tracking}
                                size="small"
                                sx={{
                                  backgroundColor: '#dcfce7',
                                  color: '#166534',
                                  fontFamily: 'monospace',
                                  fontWeight: 600
                                }}
                              />
                            </Grid>
                            <Grid item xs={3}>
                              <Typography variant="caption" sx={{ color: '#6b7280' }}>
                                Status: {order.status}
                              </Typography>
                            </Grid>
                            <Grid item xs={2}>
                              <Chip
                                label="NEW"
                                size="small"
                                sx={{
                                  backgroundColor: '#fbbf24',
                                  color: '#92400e',
                                  fontWeight: 600
                                }}
                              />
                            </Grid>
                          </Grid>
                        </Paper>
                      ))}
                    </Box>
                  </Box>
                )}

                {trackingSyncResults.success && trackingSyncResults.updatedCount === 0 && (
                  <Alert severity="info">
                    <Typography variant="body2">
                      All orders that were sent to fulfillment centers already have tracking numbers, or no tracking information is available yet.
                    </Typography>
                  </Alert>
                )}
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTrackingSyncDialogOpen(false)}>
            Close
          </Button>
          <Button 
            onClick={() => {
              setTrackingSyncDialogOpen(false);
              fetchOrders(paginationModel); // Refresh the orders list
            }}
            variant="contained"
            startIcon={<Refresh />}
          >
            Refresh Orders
          </Button>
        </DialogActions>
      </Dialog>

      {/* 🆕 NEW: MetaKocka Reconciliation Results Dialog */}
      <Dialog 
        open={reconcileDialogOpen}
        onClose={() => setReconcileDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ 
          backgroundColor: '#8b4513', 
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          <RestartAlt />
          MetaKocka Order Search Results
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Box>
            {reconcileResults && (
              <>
                {reconcileResults.success ? (
                  <Alert severity="success" sx={{ mb: 3 }}>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      ✅ Reconciliation Successful
                    </Typography>
                    <Typography variant="body2">
                      {reconcileResults.message}
                    </Typography>
                  </Alert>
                ) : (
                  <Alert severity="error" sx={{ mb: 3 }}>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      ❌ Reconciliation Failed
                    </Typography>
                    <Typography variant="body2">
                      {reconcileResults.message}
                    </Typography>
                  </Alert>
                )}

                {reconcileResults.success && (
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={3}>
                      <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#f3f4f6' }}>
                        <Typography variant="h4" sx={{ color: '#3b82f6', fontWeight: 700 }}>
                          {reconcileResults.checkedCount || 0}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6b7280' }}>
                          Orders Checked
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={3}>
                      <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#fef3c7' }}>
                        <Typography variant="h4" sx={{ color: '#d97706', fontWeight: 700 }}>
                          {reconcileResults.foundCount || 0}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#b45309' }}>
                          Found in MetaKocka
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={3}>
                      <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#f0fdf4' }}>
                        <Typography variant="h4" sx={{ color: '#22c55e', fontWeight: 700 }}>
                          {reconcileResults.updatedCount || 0}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#16a34a' }}>
                          Database Updated
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={3}>
                      <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#fecaca' }}>
                        <Typography variant="h4" sx={{ color: '#dc2626', fontWeight: 700 }}>
                          {(reconcileResults.checkedCount || 0) - (reconcileResults.foundCount || 0)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#b91c1c' }}>
                          Not Found
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                )}

                {reconcileResults.success && reconcileResults.orders && reconcileResults.orders.length > 0 && (
                  <Box>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      Orders Found in MetaKocka:
                    </Typography>
                    <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                      {reconcileResults.orders.map((order, index) => (
                        <Paper key={index} sx={{ p: 2, mb: 1, backgroundColor: '#f8fafc' }}>
                          <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={6} md={3}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                Order IT-{order.shopify_order_number}
                              </Typography>
                              {order.previous_fc_code && order.previous_fc_code !== 'metakocka' && (
                                <Typography variant="caption" sx={{ color: '#f59e0b', display: 'block' }}>
                                  Reassigned from: {order.previous_fc_name}
                                </Typography>
                              )}
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <Chip
                                label={`MK-${order.external_order_id}`}
                                size="small"
                                sx={{
                                  backgroundColor: '#fef3c7',
                                  color: '#92400e',
                                  fontFamily: 'monospace',
                                  fontWeight: 600
                                }}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              {order.tracking_number ? (
                                <Chip
                                  label={order.tracking_number}
                                  size="small"
                                  sx={{
                                    backgroundColor: '#dcfce7',
                                    color: '#166534',
                                    fontFamily: 'monospace',
                                    fontWeight: 600
                                  }}
                                />
                              ) : (
                                <Typography variant="caption" sx={{ color: '#6b7280' }}>
                                  No tracking yet
                                </Typography>
                              )}
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <Typography variant="caption" sx={{ color: '#6b7280' }}>
                                Status: {order.status_code || 'Unknown'}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Paper>
                      ))}
                    </Box>
                  </Box>
                )}

                {reconcileResults.success && reconcileResults.updatedCount === 0 && reconcileResults.foundCount === 0 && (
                  <Alert severity="info">
                    <Typography variant="body2">
                      No orders were found in MetaKocka that need reconciliation. All pending orders either don't exist in MetaKocka or are already properly assigned and marked as sent.
                    </Typography>
                  </Alert>
                )}
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReconcileDialogOpen(false)}>
            Close
          </Button>
          <Button 
            onClick={() => {
              setReconcileDialogOpen(false);
              fetchOrders(paginationModel); // Refresh the orders list
            }}
            variant="contained"
            startIcon={<Refresh />}
          >
            Refresh Orders
          </Button>
        </DialogActions>
      </Dialog>



    </Box>
  );
};

export default OrdersPage;